import React, { useState, useEffect, useRef, useCallback } from 'react'; // Hardware Registration Flow Update
import axios from 'axios';
import ReactGA from 'react-ga4';
import './App.css';
import RaceSelector from './components/RaceSelector';
import HeaderGameSelector from './components/HeaderGameSelector'; // Optimized game mode selector
import RiderPuzzle from './components/RiderPuzzle';
import BudgetTracker from './components/BudgetTracker';
import ManualRiderModal from './components/ManualRiderModal'; // Import new modal
import RiderOverviewModal from './components/RiderOverviewModal';
import RiderHistory from './components/RiderHistory';
import HistoricalTop20Modal from './components/HistoricalTop20Modal';
import EmailLinkModal from './components/EmailLinkModal';
import { supabase } from './utils/supabaseClient';
import NotesModal from './components/NotesModal';
import seoPages from './seoPages.json';

// RACES_ORDER removed, we will dynamically use races from state when needed

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:5000/api' : '/api');
// TOTAL_BUDGET removed as a constant, now calculated inside App component based on gameMode
const GA_MEASUREMENT_ID = 'G-2G9DYSQE79';
const VALID_GAME_MODES = ['klassiekers', 'giro', 'tour'];
const GAME_MODE_PATHS = {
  klassiekers: '/klassiekers',
  giro: '/giro',
  tour: '/tour'
};
const PATH_GAME_MODES = Object.entries(GAME_MODE_PATHS).reduce((acc, [mode, path]) => {
  acc[path] = mode;
  return acc;
}, {});
const SITE_URL = 'https://kopmanpuzzel.nl';
const SEO_BY_MODE = {
  klassiekers: seoPages.klassiekers,
  giro: seoPages.giro,
  tour: seoPages.tour
};
const getCanonicalUrl = (seo) => `${SITE_URL}${seo.path === '/' ? '/' : seo.path}`;
const buildStructuredData = (seo) => {
  const url = getCanonicalUrl(seo);

  if (seo.schemaType === 'WebApplication') {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: seo.schemaName,
      url,
      applicationCategory: 'GameApplication',
      operatingSystem: 'Web',
      inLanguage: 'nl',
      description: seo.description
    };
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seo.schemaName,
    url,
    inLanguage: 'nl',
    description: seo.description,
    isPartOf: {
      '@type': 'WebApplication',
      name: seoPages.home.schemaName,
      url: getCanonicalUrl(seoPages.home),
      applicationCategory: 'GameApplication',
      operatingSystem: 'Web',
      inLanguage: 'nl'
    }
  };
};
const ensureMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
    document.head.appendChild(tag);
  }
  return tag;
};
const updatePageHead = (seo) => {
  const canonicalUrl = getCanonicalUrl(seo);

  document.title = seo.title;
  ensureMetaTag('meta[name="description"]', { name: 'description' }).setAttribute('content', seo.description);
  ensureMetaTag('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', seo.ogTitle);
  ensureMetaTag('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', seo.description);
  ensureMetaTag('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', canonicalUrl);
  ensureMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }).setAttribute('content', seo.ogTitle);
  ensureMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }).setAttribute('content', seo.description);

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl);

  let structuredData = document.getElementById('kopmanpuzzel-jsonld');
  if (!structuredData) {
    structuredData = document.createElement('script');
    structuredData.id = 'kopmanpuzzel-jsonld';
    structuredData.type = 'application/ld+json';
    document.head.appendChild(structuredData);
  }
  structuredData.textContent = JSON.stringify(buildStructuredData(seo));
};
const HOME_GAME_OPTIONS = [
  {
    mode: 'klassiekers',
    title: 'Klassiekers',
    description: 'Stel je team samen voor het Scorito klassiekerspel.',
    className: 'home-mode-card-classics'
  },
  {
    mode: 'giro',
    title: 'Giro',
    description: 'Stel je team samen voor het Scorito Giro spel.',
    className: 'home-mode-card-giro'
  },
  {
    mode: 'tour',
    title: 'Tour',
    description: 'Stel je team samen voor het Scorito Tour spel.',
    className: 'home-mode-card-tour'
  }
];
const getBudgetLimitForMode = (mode) => mode === 'giro' ? 42000000 : 45000000;
const formatBudgetShort = (amount) => `${(amount / 1000000).toFixed(2)}M`;
const getRouteGameMode = () => {
  if (typeof window === 'undefined') return null;
  const normalizedPath = window.location.pathname.replace(/\/$/, '') || '/';
  return PATH_GAME_MODES[normalizedPath] || null;
};
const isHomeRoute = () => {
  if (typeof window === 'undefined') return false;
  const normalizedPath = window.location.pathname.replace(/\/$/, '') || '/';
  return normalizedPath === '/';
};
const isGrandTourMode = (mode) => mode === 'giro' || mode === 'tour';

// --- Utility Functions ---
const isGameDataEmpty = (data) => {
  if (!data) return true;
  // Check if favorites are empty
  const noFavs = !data.favoriteRiders || Object.values(data.favoriteRiders).every(arr => arr.length === 0);
  // Check if configurations have no selected riders
  const noConfigs = !data.configurations || data.configurations.every(c => (c.selectedRiders || []).length === 0);
  // Check if notes are empty
  const noNotes = !data.userNotes || Object.values(data.userNotes).every(v => !v || v.trim() === "");
  // Check for custom riders (manual additions)
  const noCustom = !data.customRiders || data.customRiders.length === 0;

  return noFavs && noConfigs && noNotes && noCustom;
};

function App() {
  const lastUserId = useRef(null);
  const lastUserEmail = useRef(null);
  const lastSyncedToken = useRef(null);
  const isLocalAuthAction = useRef(false);
  const isFirstAuthCheck = useRef(true);
  const guestMigrationPromise = useRef(null);
  const cloudStateHashes = useRef({});
  const cloudStateExists = useRef({});
  const cloudStateLoaded = useRef({});
  const gameMutationVersions = useRef({});
  const savedMutationVersions = useRef({});

  const [isHomePage, setIsHomePage] = useState(() => isHomeRoute());
  const [activeTab, setActiveTab] = useState('setup'); // 'setup' or 'puzzle'
  const [gameMode, setGameMode] = useState(() => {
    const routeMode = getRouteGameMode();
    if (routeMode) return routeMode;

    // Check localStorage first (cross-session preference), then sessionStorage (current session)
    const lastMode = localStorage.getItem('kopmanpuzzel_last_mode') ||
      sessionStorage.getItem('kopmanpuzzel_last_mode');
    return (lastMode && VALID_GAME_MODES.includes(lastMode)) ? lastMode : 'klassiekers';
  });

  const isGrandTour = isGrandTourMode(gameMode);
  const grandTourName = gameMode === 'tour' ? 'Tour de France' : "Giro d'Italia";
  const grandTourShortName = gameMode === 'tour' ? 'Tour' : 'Giro';
  const currentSeo = isHomePage ? seoPages.home : (SEO_BY_MODE[gameMode] || seoPages.klassiekers);
  const totalBudget = gameMode === 'giro' ? 42000000 : 45000000;
  const [races, setRaces] = useState([]);
  const [favoriteRiders, setFavoriteRiders] = useState({}); // { race: [{rider, rank}] }
  const [riders, setRiders] = useState({});
  const [homePriceMaps, setHomePriceMaps] = useState({});
  const [competitions, setCompetitions] = useState([]); // races_id_2026 data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdownRace, setOpenDropdownRace] = useState(null); // Slechts 1 dropdown tegelijk

  // Manual Riders State
  const [customRiders, setCustomRiders] = useState([]); // [{rider, race}]
  const [showManualModal, setShowManualModal] = useState(false);
  const [showInvalidRiderWarning, setShowInvalidRiderWarning] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);

  // Rider Overview shared state
  const [overviewSortBy, setOverviewSortBy] = useState('price');
  const [overviewActiveRaceSorts, setOverviewActiveRaceSorts] = useState([]);
  const [overviewShowFavoritesFirst, setOverviewShowFavoritesFirst] = useState(false);

  // configuration management
  const [configurations, setConfigurations] = useState([
    {
      id: 1,
      selectedRiders: [],
      plannedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
      selectedSwapAfterRace: { '0': null, '1': null, '2': null },
      appliedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
      appliedSelectedSwapAfterRace: { '0': null, '1': null, '2': null },
      name: ''
    }
  ]);
  const [activeConfigId, setActiveConfigId] = useState(1);
  // nextConfigId removed (unused)

  // Get current configuration
  const activeConfig = configurations.find(c => c.id === activeConfigId);
  const selectedRiders = activeConfig?.selectedRiders || [];

  // RiderPuzzle state that persists across tab switches
  const [sortBy, setSortBy] = useState('price'); // 'price' or 'race_count' or 'name'
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [filterSelected, setFilterSelected] = useState(false);
  const [selectedRaces, setSelectedRaces] = useState([]);
  const [tableView, setTableView] = useState('coverage'); // 'coverage' or 'favorites'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeams, setSelectedTeams] = useState([]);

  // User Actions Dropdown state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userProfileRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userProfileRef.current && !userProfileRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showUserMenu]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfigId, setDeleteConfigId] = useState(null);

  // Reset confirmation dialog
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Welcome message for setup
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const hasSeenWelcome = useRef({ klassiekers: false, giro: false, tour: false });

  // Welcome message for puzzle
  const [showPuzzleWelcomeMessage, setShowPuzzleWelcomeMessage] = useState(false);

  const hasSeenPuzzleWelcome = useRef({ klassiekers: false, giro: false, tour: false });

  // Auth State
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('kopmanpuzzel_user');
    if (savedUser) {
      try { return JSON.parse(savedUser); } catch (e) { return null; }
    }
    return null;
  });
  const [showSessionMismatchModal] = useState(false);
  const [fullCloudData, setFullCloudData] = useState({}); // Stores data for ALL games from server
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [showMigrationModalDelayed, setShowMigrationModalDelayed] = useState(false);
  const [migrationPendingData, setMigrationPendingData] = useState(null);

  // Trigger delayed migration modal after loading is complete
  useEffect(() => {
    if (!loading && showMigrationModalDelayed && user) {
      setShowMigrationModal(true);
      setShowMigrationModalDelayed(false);
    }
  }, [loading, showMigrationModalDelayed, user]);
  const [authLoading, setAuthLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [registerError, setRegisterError] = useState(null);
  const [migrationError, setMigrationError] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(null);
  const [email, setEmail] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Toast auto-hide logic
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const triggerToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // Puzzle-internal modal state for scroll locking
  const [isPuzzleModalOpen, setIsPuzzleModalOpen] = useState(false);
  const [modalStack, setModalStack] = useState([]); // [{ type: 'top20'|'history', data: { ... }, id: ... }]

  // Auth Form State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Notes Feature State
  const [userNotes, setUserNotes] = useState({ algemeen: '' });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesTarget, setNotesTarget] = useState('algemeen');

  // Welcome Message Preferences
  const [dontShowAgainSetup, setDontShowAgainSetup] = useState(false);

  // Persistence Control Refs
  const lastSavedJson = useRef(''); // Tracks last state sent to storage/cloud
  const lastLoadedMode = useRef(null);
  const isRestoring = useRef(false); // Flag to prevent auto-save from triggering during a restoration
  const gameModeRef = useRef(gameMode);
  gameModeRef.current = gameMode; // Altijd in sync houden met state
  const dataGameMode = useRef(null); // Tracks which gameMode the current races/riders data belongs to
  const isGameModeLoading = loading || dataGameMode.current !== gameMode || lastLoadedMode.current !== gameMode;

  const markGameModified = useCallback((mode = gameModeRef.current) => {
    gameMutationVersions.current[mode] = (gameMutationVersions.current[mode] || 0) + 1;
  }, []);
  // Check for invalid favorites (riders not in startlist for that race)
  const { invalidFavorites, hasInvalidFavorites } = React.useMemo(() => {
    const invalidMap = {};
    let hasInvalid = false;

    races.forEach(race => {
      const raceFavorites = favoriteRiders[race] || [];
      const invalidForRace = raceFavorites.filter(fav => {
        const riderInfo = riders[fav.rider];

        // Check API races (ensure riderInfo exists)
        const isInApiStartlist = riderInfo && riderInfo.races && Array.isArray(riderInfo.races) && riderInfo.races.includes(race);

        // Check Custom Riders
        const isInCustomRiders = customRiders.some(cr => cr.rider === fav.rider && cr.race === race);

        // Invalid if NOT in EITHER
        return !(isInApiStartlist || isInCustomRiders);
      });

      if (invalidForRace.length > 0) {
        invalidMap[race] = invalidForRace.map(f => f.rider);
        hasInvalid = true;
      }
    });

    return { invalidFavorites: invalidMap, hasInvalidFavorites: hasInvalid };
  }, [races, favoriteRiders, riders, customRiders]);

  // Flat list of all invalid riders for Step 2 highlighting
  const allInvalidRiderNames = React.useMemo(() => {
    const set = new Set();
    Object.values(invalidFavorites).forEach(list => {
      list.forEach(name => set.add(name));
    });
    return Array.from(set);
  }, [invalidFavorites]);

  // --- State Helpers & Restoration (Moved up for initialization safety) ---
  const getDefaultGameState = React.useCallback(() => ({
    favoriteRiders: {},
    configurations: [{
      id: 1,
      selectedRiders: [],
      plannedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
      selectedSwapAfterRace: { '0': null, '1': null, '2': null },
      appliedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
      appliedSelectedSwapAfterRace: { '0': null, '1': null, '2': null },
      name: ''
    }],
    customRiders: []
  }), []);

  const getGameStateObject = React.useCallback(() => {
    const isGrandTourGame = isGrandTourMode(gameMode);
    const filteredFavorites = {};
    races.forEach(race => { filteredFavorites[race] = favoriteRiders[race] || []; });
    const cleanedConfigs = configurations.map(config => {
      const base = { id: config.id, name: config.name || '', selectedRiders: config.selectedRiders || [] };
      if (isGrandTourGame) return base;
      const ensureThreeSwaps = (list) => {
        const baseList = Array.isArray(list) ? [...list] : [];
        while (baseList.length < 3) baseList.push({ out: '', in: '' });
        return baseList;
      };
      return {
        ...base,
        plannedSwaps: ensureThreeSwaps(config.plannedSwaps),
        selectedSwapAfterRace: config.selectedSwapAfterRace || {},
        appliedSwaps: ensureThreeSwaps(config.appliedSwaps),
        appliedSelectedSwapAfterRace: config.appliedSelectedSwapAfterRace || {}
      };
    });
    const state = {
      favoriteRiders: filteredFavorites,
      configurations: cleanedConfigs,
      activeConfigId,
      userNotes: userNotes || { algemeen: '' }
    };
    if (!isGrandTourGame) state.customRiders = customRiders;
    return state;
  }, [favoriteRiders, configurations, activeConfigId, customRiders, gameMode, races, userNotes]);

  const getMultiGameState = React.useCallback(() => {
    const storage = user ? localStorage : sessionStorage;
    const existingRaw = storage.getItem('kopmanpuzzel_multi_state');
    let multiState = { games: {} };
    if (existingRaw) {
      try { multiState = JSON.parse(existingRaw); } catch (e) { multiState = { games: {} }; }
    }
    const current = getGameStateObject();
    return {
      ...multiState,
      games: {
        ...(multiState.games || {}),
        [gameMode]: {
          favoriteRiders: current.favoriteRiders,
          configurations: current.configurations,
          customRiders: current.customRiders,
          userNotes: current.userNotes,
          activeConfigId: current.activeConfigId
        }
      }
    };
  }, [user, gameMode, getGameStateObject]);


  useEffect(() => {
    if (!isHomePage || !user) return;

    const modesToFetch = VALID_GAME_MODES.filter(mode => !homePriceMaps[mode]);
    if (modesToFetch.length === 0) return;

    let cancelled = false;
    const normalizedApiUrl = API_URL.trim().replace(/\/$/, '');

    Promise.all(
      modesToFetch.map(async mode => {
        const res = await axios.get(`${normalizedApiUrl}/riders?game=${mode}`);
        return [mode, res.data || {}];
      })
    ).then(entries => {
      if (cancelled) return;
      setHomePriceMaps(prev => {
        const next = { ...prev };
        entries.forEach(([mode, priceMap]) => {
          next[mode] = priceMap;
        });
        return next;
      });
    }).catch(err => {
      console.error('Failed to load homepage price maps', err);
    });

    return () => { cancelled = true; };
  }, [isHomePage, user, homePriceMaps]);

  const homeGameSummaries = React.useMemo(() => {
    const summaries = {};
    let guestGames = {};

    if (!user) {
      const guestRaw = sessionStorage.getItem('kopmanpuzzel_multi_state') || localStorage.getItem('kopmanpuzzel_multi_state');
      if (guestRaw) {
        try {
          guestGames = JSON.parse(guestRaw)?.games || {};
        } catch (e) {
          guestGames = {};
        }
      }
    }

    HOME_GAME_OPTIONS.forEach(({ mode }) => {
      const hasLoadedAccountData = user ? Object.prototype.hasOwnProperty.call(fullCloudData, mode) : true;
      const gameData = user ? (fullCloudData[mode] || {}) : (guestGames[mode] || {});
      const configs = Array.isArray(gameData.configurations) ? gameData.configurations : [];
      const activeId = gameData.activeConfigId || configs[0]?.id;
      const activeSavedConfig = configs.find(config => config.id === activeId) || configs[0] || {};
      const selected = Array.isArray(activeSavedConfig.selectedRiders) ? activeSavedConfig.selectedRiders : [];
      const fallbackPriceMap = mode === gameMode ? riders : {};
      const priceMap = homePriceMaps[mode] || fallbackPriceMap;
      const spent = selected.reduce((sum, rider) => {
        const riderInfo = priceMap[rider];
        const price = typeof riderInfo === 'object' ? riderInfo.price : riderInfo;
        return sum + (price || 500000);
      }, 0);
      const total = getBudgetLimitForMode(mode);

      summaries[mode] = {
        isLoading: user && !hasLoadedAccountData,
        riderCount: selected.length,
        spent,
        total,
        isOverBudget: spent > total
      };
    });

    return summaries;
  }, [user, fullCloudData, homePriceMaps, gameMode, riders]);
  const restoreGameState = React.useCallback((data, isFromCloud = false, forcedMode = null) => {
    if (!data) return;
    isRestoring.current = true;
    try {
      const effectiveMode = forcedMode || gameMode;
      lastLoadedMode.current = effectiveMode;
      let targetData = data;
      if (isFromCloud) {
        if (VALID_GAME_MODES.some(mode => data[mode])) {
          setFullCloudData(data);
          targetData = data[effectiveMode] || {};
        } else {
          setFullCloudData(prev => ({ ...prev, [effectiveMode]: data }));
          targetData = data;
        }
      }
      const rawFavs = targetData.favoriteRiders || {};
      const normalizedFavs = { ...rawFavs };
      races.forEach(race => { if (!normalizedFavs[race] || !Array.isArray(normalizedFavs[race])) { normalizedFavs[race] = []; } });
      setFavoriteRiders(normalizedFavs);
      if (targetData.configurations && Array.isArray(targetData.configurations)) {
        const defaultState = getDefaultGameState();
        const restoredConfigs = targetData.configurations.map(config => ({
          ...defaultState.configurations[0],
          ...config
        }));
        setConfigurations(restoredConfigs);
      } else {
        setConfigurations(getDefaultGameState().configurations);
      }
      setCustomRiders(Array.isArray(targetData.customRiders) ? targetData.customRiders : []);
      setActiveConfigId(targetData.activeConfigId || 1);
      setUserNotes(targetData.userNotes || { algemeen: '' });
      savedMutationVersions.current[effectiveMode] = gameMutationVersions.current[effectiveMode] || 0;
    } finally {
      setTimeout(() => { isRestoring.current = false; }, 0);
    }
  }, [gameMode, races, getDefaultGameState]);

  // --- Auth Handlers & Sync (Moved up to fix ReferenceError) ---
  const syncGameWithMigration = React.useCallback(async (token, mode, cloudData = null, guestModeDataOverride = undefined, applyToUi = true) => {
    try {
      let finalData = cloudData;
      let stateHash = cloudStateHashes.current[mode] || null;
      let stateExists = cloudStateExists.current[mode] || false;

      if (finalData === null || finalData === undefined) {
        const apiUrlClean = API_URL.trim().replace(/\/$/, "");
        const res = await axios.get(`${apiUrlClean}/login-data?game=${mode}&v=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        });
        finalData = res.data?.data || {};
        stateHash = res.data?.state_hash || null;
        stateExists = Boolean(res.data?.state_exists);
        cloudStateHashes.current[mode] = stateHash;
        cloudStateExists.current[mode] = stateExists;
        cloudStateLoaded.current[mode] = true;
      }
      let guestModeData = guestModeDataOverride;
      if (guestModeData === undefined) {
        const guestDataRaw = sessionStorage.getItem('kopmanpuzzel_multi_state');
        guestModeData = null;
        if (guestDataRaw) {
          try {
            const parsed = JSON.parse(guestDataRaw);
            guestModeData = parsed?.games?.[mode];
          } catch (e) {
            console.error("Failed to parse guest data for migration", e);
          }
        }
      }
      const cloudIsEmpty = isGameDataEmpty(finalData);
      const guestIsEmpty = isGameDataEmpty(guestModeData);
      if (cloudIsEmpty && !guestIsEmpty) {
        console.log(`Migrating guest data to cloud for ${mode}...`);
        finalData = guestModeData;
        const apiUrlClean = API_URL.trim().replace(/\/$/, "");
        const migrationResponse = await axios.post(`${apiUrlClean}/save-game-state`, {
          gameState: { [mode]: guestModeData },
          expectedStateHashes: stateExists && stateHash ? { [mode]: stateHash } : {}
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        });
        cloudStateHashes.current[mode] = migrationResponse.data?.state_hashes?.[mode] || null;
        cloudStateExists.current[mode] = true;
        cloudStateLoaded.current[mode] = true;
      }
      setFullCloudData(prev => ({ ...prev, [mode]: finalData }));
      if (applyToUi && mode === gameModeRef.current) {
        restoreGameState(finalData, true, mode);
        lastLoadedMode.current = mode;
        setTimeout(() => {
          lastSavedJson.current = JSON.stringify(getMultiGameState());
        }, 100);
      }
      return finalData;
    } catch (e) {
      console.error(`Error in syncGameWithMigration for ${mode}:`, e);
      return null;
    }
  }, [restoreGameState, getMultiGameState]);

  const syncLoginActiveGame = React.useCallback(async (token, guestMultiState) => {
    const guestGames = guestMultiState?.games || {};
    const activeMode = gameModeRef.current;

    const loadActiveMode = () => syncGameWithMigration(
      token,
      activeMode,
      null,
      guestGames[activeMode] ?? null,
      true
    );

    let activeData = await loadActiveMode();
    if (activeData === null || activeData === undefined) {
      await new Promise(resolve => setTimeout(resolve, 500));
      activeData = await loadActiveMode();
    }

    const activeSucceeded = activeData !== null && activeData !== undefined;
    const remainingModes = VALID_GAME_MODES.filter(mode => mode !== activeMode);

    Promise.all(
      remainingModes.map(async mode => [
        mode,
        await syncGameWithMigration(token, mode, null, guestGames[mode] ?? null, false)
      ])
    ).then(syncResults => {
      const remainingSucceeded = syncResults.every(([, data]) => data !== null && data !== undefined);
      if (activeSucceeded && remainingSucceeded) {
        sessionStorage.removeItem('kopmanpuzzel_multi_state');
      } else {
        console.error("Guest data migration was incomplete; local guest data has been retained.");
      }
    });

    return activeSucceeded;
  }, [syncGameWithMigration]);
  const fetchUserCloudData = React.useCallback(async (token, modeToLoad = null) => {
    const mode = modeToLoad || gameModeRef.current;
    setLoading(true);
    try {
      const activeData = await syncGameWithMigration(token, mode);
      if (!activeData) {
        await new Promise(r => setTimeout(r, 500));
        await syncGameWithMigration(token, mode);
      }
    } catch (e) {
      console.error("Initial cloud load failed", e);
    } finally {
      setLoading(false);
    }
  }, [syncGameWithMigration]);

  const handleSessionChange = React.useCallback((session) => {
    if (!session) {
      lastUserId.current = null;
      lastUserEmail.current = null;
      lastSyncedToken.current = null;
    }
    if (session) {
      const sbUser = session.user;
      const newUserEmail = sbUser.email;
      const newToken = session.access_token;
      const emailChanged = newUserEmail !== lastUserEmail.current;
      const tokenChanged = newToken !== lastSyncedToken.current;
      const wasGuest = lastUserId.current === null;
      let guestMultiState = null;
      if (wasGuest) {
        const guestDataRaw = sessionStorage.getItem('kopmanpuzzel_multi_state');
        if (guestDataRaw) {
          try {
            guestMultiState = JSON.parse(guestDataRaw);
          } catch (e) {
            console.error("Failed to capture guest data before login", e);
          }
        }
      }
      const newUser = {
        name: sbUser.user_metadata?.username || sbUser.email.split('@')[0],
        id: sbUser.id,
        email: sbUser.email,
        access_token: session.access_token
      };
      if (!isLocalAuthAction.current && lastUserId.current && lastUserId.current !== sbUser.id) {
        console.warn("Session ID mismatch detected (suppressed):", lastUserId.current, "vs", sbUser.id);
        // setShowSessionMismatchModal(true);
        // return;
      }
      if (wasGuest) {
        // Keep rendering and autosave blocked until the active account game state is known.
        setLoading(true);
        isRestoring.current = true;
      }
      setUser(newUser);
      localStorage.setItem('kopmanpuzzel_user', JSON.stringify(newUser));
      if (tokenChanged || emailChanged) {
        const userIdChanged = lastUserId.current !== sbUser.id;
        const shouldForceStep1 = wasGuest || userIdChanged || emailChanged;

        lastUserId.current = sbUser.id;
        lastUserEmail.current = newUserEmail;
        lastSyncedToken.current = newToken;

        // Only clear these if the USER actually changed.
        // A simple token refresh shouldn't invalidate the last saved/loaded markers.
        if (userIdChanged || wasGuest) {
          lastLoadedMode.current = null;
          lastSavedJson.current = "";
        }

        if (wasGuest) {
          guestMigrationPromise.current = syncLoginActiveGame(session.access_token, guestMultiState)
            .then(async activeSucceeded => {
              if (!activeSucceeded && lastLoadedMode.current !== gameModeRef.current) {
                await fetchUserCloudData(session.access_token, gameModeRef.current);
              }
              return activeSucceeded;
            })
            .finally(() => {
              guestMigrationPromise.current = null;
              setLoading(false);
            });
        } else if (userIdChanged || emailChanged) {
          syncGameWithMigration(session.access_token, gameModeRef.current);
        }

        if (!isFirstAuthCheck.current && shouldForceStep1) {
          setActiveTab('setup');
        }
      }
      isFirstAuthCheck.current = false;
      if (sbUser.email?.toLowerCase().endsWith('@kopmanpuzzel.nl')) {
        const promptKey = `kopman_migration_prompt_shown_${sbUser.id}`;
        if (!localStorage.getItem(promptKey) && !sbUser.user_metadata?.is_new_user) {
          setMigrationPendingData({
            username: sbUser.user_metadata?.username || sbUser.email.split('@')[0],
            password: ''
          });
          setShowMigrationModal(true);
          localStorage.setItem(promptKey, 'true');
        }
      }
      isFirstAuthCheck.current = false;
    } else {
      if (!isLocalAuthAction.current && lastUserId.current) {
        console.warn("Session lost or cleared (suppressed mismatch modal)");
        // setShowSessionMismatchModal(true);
      }
      isFirstAuthCheck.current = false;
    }
  }, [syncGameWithMigration, syncLoginActiveGame, fetchUserCloudData]);

  const handleSessionChangeRef = useRef(handleSessionChange);
  useEffect(() => {
    handleSessionChangeRef.current = handleSessionChange;
  }, [handleSessionChange]);

  // --- Supabase Auth Listener ---
  useEffect(() => {
    // 1. Capture initial hash to identify if this tab is the one for recovery
    const initialHash = window.location.hash;
    const isRecoveryTab = initialHash.includes('type=recovery') || window.location.search.includes('type=recovery');

    // 2. Check for errors in URL hash (e.g. expired recovery link)
    if (initialHash && (initialHash.includes('error=') || initialHash.includes('error_description='))) {
      try {
        const hashContent = initialHash.startsWith('#') ? initialHash.substring(1) : initialHash;
        const params = new URLSearchParams(hashContent);
        const errorMsg = params.get('error_description') || params.get('error') || "Onbekende fout bij verwerken van de link.";

        console.log("Caught Auth Error in URL hash:", errorMsg);
        setResetError(errorMsg.toLowerCase().includes('expired') || errorMsg.toLowerCase().includes('invalid')
          ? "De link is verlopen of al een keer gebruikt. Vraag a.u.b. een nieuwe aan."
          : errorMsg);
        setShowResetPasswordModal(true);

        window.history.replaceState(null, null, window.location.pathname + window.location.search);
      } catch (e) {
        console.error("Error parsing URL hash:", e);
      }
    }

    // 3. Handle session changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Multi-tab Safety: Only show if this SPECIFIC tab was opened via recovery link
        if (isRecoveryTab) {
          setShowUpdatePasswordModal(true);
          setShowLoginModal(false);
          setShowRegisterModal(false);
        }
      }

      // If we know this event came from a local action (this tab),
      // we allow handleSessionChange to update our local state without triggering 'mismatch'
      handleSessionChangeRef.current(session);

      // Reset the flag after handleSessionChange has processed the event
      if (isLocalAuthAction.current) {
        isLocalAuthAction.current = false;
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSessionChangeRef.current(session);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSensitiveUserData = () => {
    // 1. CLEAR PUZZLE DATA (Crucial for privacy and isolation)
    localStorage.removeItem('kopmanpuzzel_multi_state');
    localStorage.removeItem('kopmanpuzzel_user');

    // 2. CLEAR SESSION (Guest data)
    sessionStorage.removeItem('kopmanpuzzel_multi_state');

    // 3. CLEAR SUPABASE AUTH
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('auth-token') || key.startsWith('supabase.auth'))) {
        localStorage.removeItem(key);
      }
    }
  };



  // Synchronous scroll-locking helper
  const updateScrollLock = (isOpen) => {
    if (isOpen) {
      document.documentElement.classList.add('no-scroll');
      document.body.classList.add('no-scroll');
    } else {
      document.documentElement.classList.remove('no-scroll');
      document.body.classList.remove('no-scroll');
    }
  };

  // Lock body scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen =
      showManualModal ||
      showLoginModal ||
      showRegisterModal ||
      showWelcomeMessage ||
      showPuzzleWelcomeMessage ||
      showDeleteConfirm ||
      showResetConfirm ||
      showInvalidRiderWarning ||
      isPuzzleModalOpen ||
      modalStack.length > 0 ||
      showSessionMismatchModal;

    updateScrollLock(isAnyModalOpen);

    return () => {
      // Clean up if component unmounts
      document.documentElement.classList.remove('no-scroll');
      document.body.classList.remove('no-scroll');
    };
  }, [
    showManualModal,
    showLoginModal,
    showRegisterModal,
    showWelcomeMessage,
    showPuzzleWelcomeMessage,
    showDeleteConfirm,
    showResetConfirm,
    showInvalidRiderWarning,
    isPuzzleModalOpen,
    modalStack.length,
    showSessionMismatchModal,
    showNotesModal
  ]);

  // --- Google Analytics ---
  useEffect(() => {
    ReactGA.initialize(GA_MEASUREMENT_ID);
    // Initial page load hit
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
      title: "Kopmanpuzzel"
    });
  }, []);

  // Track route/tab changes as virtual page views and keep route metadata current.
  useEffect(() => {
    const seo = isHomePage ? seoPages.home : (SEO_BY_MODE[gameMode] || seoPages.klassiekers);
    updatePageHead(seo);

    if (isHomePage) {
      ReactGA.send({
        hitType: "pageview",
        page: '/',
        title: seo.title
      });
      return;
    }

    const routePrefix = GAME_MODE_PATHS[gameMode] || '/klassiekers';
    const stepTitle = activeTab === 'setup' ? `${seo.title} | Stap 1: Favorieten` : `${seo.title} | Stap 2: Puzzel`;

    ReactGA.send({
      hitType: "pageview",
      page: activeTab === 'setup' ? `${routePrefix}/stap-1` : `${routePrefix}/stap-2`,
      title: stepTitle
    });
  }, [activeTab, gameMode, isHomePage]);
  // Track Welcome Modal Read Time (Step 1)
  const welcomeStartTime = useRef(null);
  useEffect(() => {
    if (showWelcomeMessage) {
      welcomeStartTime.current = Date.now();
      // Track View Event
      ReactGA.event("welcome_v_1", {
        label: "Step 1 Welcome Shown"
      });
    } else if (welcomeStartTime.current) {
      const duration = Math.round((Date.now() - welcomeStartTime.current) / 1000);
      ReactGA.event("welcome_r_1", {
        value: duration,
        label: "Step 1 Welcome Duration (s)"
      });
      welcomeStartTime.current = null;
    }
  }, [showWelcomeMessage]);

  // Track Puzzle Welcome Modal Read Time (Step 2)
  const puzzleWelcomeStartTime = useRef(null);
  useEffect(() => {
    if (showPuzzleWelcomeMessage) {
      puzzleWelcomeStartTime.current = Date.now();
      // Track View Event
      ReactGA.event("welcome_v_2", {
        label: "Step 2 Welcome Shown"
      });
    } else if (puzzleWelcomeStartTime.current) {
      const duration = Math.round((Date.now() - puzzleWelcomeStartTime.current) / 1000);
      ReactGA.event("welcome_r_2", {
        value: duration,
        label: "Step 2 Welcome Duration (s)"
      });
      puzzleWelcomeStartTime.current = null;
    }
  }, [showPuzzleWelcomeMessage]);

  // --- Persistence Logic ---



  const save_game_state = async (multiDataToSave = null, modeToSave = gameMode, gameDataOverride = null) => {
    if (!user || !user.id || !user.access_token) return false;
    const mutationVersion = gameMutationVersions.current[modeToSave] || 0;
    const savedVersion = savedMutationVersions.current[modeToSave] || 0;
    if (mutationVersion <= savedVersion) return true;
    if (!cloudStateLoaded.current[modeToSave]) {
      console.warn(`Blocked save for ${modeToSave}: account state has not been loaded yet.`);
      return false;
    }

    const payload = multiDataToSave || getMultiGameState();
    const gameData = gameDataOverride || payload?.games?.[modeToSave] ||
      (modeToSave === gameMode ? getGameStateObject() : null);

    if (!gameData) return false;
    if (!cloudStateExists.current[modeToSave] && isGameDataEmpty(gameData)) {
      return true;
    }

    const expectedHash = cloudStateHashes.current[modeToSave];
    const expectedStateHashes = cloudStateExists.current[modeToSave] && expectedHash
      ? { [modeToSave]: expectedHash }
      : {};

    try {
      const response = await axios.post(`${API_URL}/save-game-state`, {
        gameState: { [modeToSave]: gameData },
        expectedStateHashes
      }, {
        headers: { Authorization: `Bearer ${user.access_token}` },
        timeout: 15000
      });

      cloudStateHashes.current[modeToSave] = response.data?.state_hashes?.[modeToSave] || null;
      cloudStateExists.current[modeToSave] = true;
      setFullCloudData(prev => ({ ...prev, [modeToSave]: gameData }));
      savedMutationVersions.current[modeToSave] = mutationVersion;
      lastSavedJson.current = JSON.stringify(payload);
      console.log(`Cloud Save [${modeToSave}]: Success`);
      return true;
    } catch (e) {
      console.error(`Cloud save failed for ${modeToSave}`, e);
      if (e.response?.status === 409) {
        triggerToast('Opslaan geblokkeerd omdat nieuwere accountdata is gevonden. De accountdata wordt opnieuw geladen.', 'error');
        await syncGameWithMigration(user.access_token, modeToSave, null, null);
      }
      return false;
    }
  };


  // 1. Initial Load & User restoration (Now handled by useState initializers)
  useEffect(() => {
    // This effect is kept empty or for other mount-time logic
    // to avoid breaking dependencies if they exist elsewhere.
  }, []);

  // Master Lifecycle Effect: Orchestrates all persistent data loading
  useEffect(() => {
    const performLoad = async () => {
      setLoading(true);
      try {
        if (user && user.access_token) {
          // Login migration owns the initial cloud load. Wait for it instead of
          // starting a competing fetch for the active game mode.
          if (guestMigrationPromise.current) {
            await guestMigrationPromise.current;
          } else if (lastLoadedMode.current !== gameMode) {
            console.log(`Syncing ${gameMode} from cloud...`);
            // Even if we have fullCloudData, if this is a fresh login (lastLoadedMode is null),
            // we force a fetch to ensure sync.
            if (!fullCloudData[gameMode] || isGameDataEmpty(fullCloudData[gameMode]) || lastLoadedMode.current === null) {
              await fetchUserCloudData(user.access_token, gameMode);
            } else {
              restoreGameState(fullCloudData[gameMode], true, gameMode);
            }
          }
        } else {
          // Guest users: restore this mode, or explicitly mark a fresh mode as ready.
          const savedRaw = sessionStorage.getItem('kopmanpuzzel_multi_state');
          let restoredGuestMode = false;
          if (savedRaw) {
            try {
              const multiState = JSON.parse(savedRaw);
              if (multiState.games && multiState.games[gameMode]) {
                restoreGameState(multiState.games[gameMode], false, gameMode);
                restoredGuestMode = true;
              }
            } catch (e) {
              console.error("Failed to restore guest state", e);
            }
          }
          if (!restoredGuestMode) {
            restoreGameState(getDefaultGameState(), false, gameMode);
          }
        }
      } catch (err) {
        console.error("Critical loading error in Master Controller:", err);
      } finally {
        setLoading(false);
      }
    };

    performLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, gameMode]);

  // 2b. Listen for changes in other tabs (Cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // The 'storage' event only triggers when things are changed in ANOTHER tab
      if (e.key === 'kopmanpuzzel_multi_state' && e.newValue) {
        if (isRestoring.current) return;

        try {
          const multiState = JSON.parse(e.newValue);
          if (multiState.games && multiState.games[gameMode]) {
            const incomingData = multiState.games[gameMode];

            // Only restore if the data is actually different from current state
            const currentJson = JSON.stringify(getGameStateObject());
            const incomingJson = JSON.stringify(incomingData);

            if (currentJson !== incomingJson) {
              console.log("Syncing state from other tab...");
              restoreGameState(incomingData);
            }
          }
        } catch (err) {
          console.error("Cross-tab sync failed", err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [gameMode, restoreGameState, getGameStateObject]);

  // 3. Auto-save effect: Handles both local/session persistence AND Cloud sync
  useEffect(() => {
    if (loading || isRestoring.current) return;

    // A. Local preference (Immediate side persistence)
    // We do this immediately to ensure that if the user refreshes or closes the tab,
    // their changes are at least saved locally and can be recovered/synced later.
    const multiData = getMultiGameState();
    const multiDataJson = JSON.stringify(multiData);

    if (multiDataJson !== lastSavedJson.current) {
      const storage = user ? localStorage : sessionStorage;
      localStorage.setItem('kopmanpuzzel_last_mode', gameMode);
      storage.setItem('kopmanpuzzel_multi_state', multiDataJson);
      if (user) {
        localStorage.setItem('kopmanpuzzel_user', JSON.stringify(user));
      }
    }

    const timeoutId = setTimeout(() => {
      // B. Cloud sync (if logged in)
      if (loading || isRestoring.current || lastLoadedMode.current !== gameMode || dataGameMode.current !== gameMode) {
        return;
      }
      const mutationVersion = gameMutationVersions.current[gameMode] || 0;
      const savedVersion = savedMutationVersions.current[gameMode] || 0;
      if (mutationVersion <= savedVersion || multiDataJson === lastSavedJson.current) {
        return;
      }
      save_game_state(multiData);
    }, 1000); // Debounce cloud save slightly more to prevent rate limiting

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    favoriteRiders, configurations, activeConfigId, sortBy, filterFavorites,
    filterSelected, selectedRaces, tableView, user, loading, customRiders, gameMode, userNotes
  ]);

  // Tab closure safety: Try to save one last time if the user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      const multiData = getMultiGameState();
      const multiDataJson = JSON.stringify(multiData);
      if (multiDataJson !== lastSavedJson.current) {
        const storage = user ? localStorage : sessionStorage;
        storage.setItem('kopmanpuzzel_multi_state', multiDataJson);
        // Cloud save via sendBeacon is often unreliable for complex JSON,
        // but by ensuring localStorage is updated, we can sync on next load.
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user, gameMode, getMultiGameState]);


  // Auth Handlers with Data Sync
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    if (!username || !password) {
      setLoginError("Vul een gebruikersnaam en wachtwoord in.");
      return;
    }

    setAuthLoading(true);
    isLocalAuthAction.current = true;
    try {
      console.log("Starting login for user:", username);

      let serverResponse = null;
      let legacyStatus = null;
      try {
        // 1. Check if legacy migration is needed via our backend
        const res = await axios.post(`${API_URL}/login`, { username, password });
        serverResponse = res.data;
        legacyStatus = serverResponse.status;
        console.log("Backend check status:", legacyStatus);
      } catch (err) {
        // If backend returns 401, it's either a wrong password OR it's a new user who only exists in Supabase.
        // We will let Supabase handle the final validation in the next step.
        if (err.response?.status !== 401) {
          throw new Error(err.response?.data?.error || err.message || "Server check mislukt");
        }
      }

      if (legacyStatus === 'MIGRATION_REQUIRED') {
        console.log("Performing silent background migration for:", username);

        // 1. Call backend to create proxy Supabase user
        await axios.post(`${API_URL}/migrate-user`, {
          username,
          password,
          email: null
        });

        // 2. Sign in immediately to Supabase using the ID-suffixed email from server
        const proxyEmail = serverResponse?.email || `${username.toLowerCase().replaceAll(' ', '_')}@kopmanpuzzel.nl`;
        const { error: sbError } = await supabase.auth.signInWithPassword({
          email: proxyEmail,
          password: password
        });

        if (sbError) throw sbError;

        // 3. Signal that we should show the prompt ONCE loading is done
        setMigrationPendingData({ username, password });
        setShowMigrationModalDelayed(true);
        setShowLoginModal(false);
        setUsername('');
        setPassword('');
      } else {
        // ALREADY_MIGRATED or New User (401 from backend)
        // Flexible login: Use backend-provided unique identifier if available
        const isEmail = username.includes('@');
        const loginEmail = serverResponse?.email || (isEmail ? username : `${username.toLowerCase().replaceAll(' ', '_')}@kopmanpuzzel.nl`);

        console.log("Attempting Supabase login with:", loginEmail);

        const { error: sbError } = await supabase.auth.signInWithPassword({
          email: loginEmail,
          password: password
        });

        if (sbError) {
          console.error("Supabase login error:", sbError.message);
          throw new Error("Ongeldige inloggegevens. Controleer je gebruikersnaam/e-mail en wachtwoord.");
        }

        console.log("Supabase login SUCCESSFUL");
        // Success handled by Auth Listener in useEffect
        setShowLoginModal(false);
        setUsername('');
        setPassword('');
        ReactGA.event("login_success_supabase");
      }
    } catch (err) {
      console.error(err);
      setLoginError(err.message || "Inloggen mislukt.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleMigrate = async (email = null) => {
    // Determine credentials and authorization
    const passwordToUse = migrationPendingData?.password || null;
    const usernameToUse = migrationPendingData?.username || user?.name;
    const accessToken = user?.access_token || null;

    if (!usernameToUse) {
      alert("Gebruikersnaam onbekend.");
      return;
    }

    // Security check: We need EITHER a password (initial login) OR a token (already logged in)
    if (!passwordToUse && !accessToken) {
      alert("Sessie verlopen. Log a.u.b. opnieuw in.");
      return;
    }

    setAuthLoading(true);

    try {
      // 1. Tell backend to create Supabase user and link it
      // We pass the JWT if we have it, which allows the backend to skip the password check
      const res = await axios.post(`${API_URL.trim()}/migrate-user`, {
        username: usernameToUse,
        password: passwordToUse,
        email: email // can be null
      }, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
      });

      if (res.data.status === 'SUCCESS') {
        const passwordForLogin = passwordToUse; // This might be null if we are already logged in

        // 2. If we aren't already logged in with a proxy, we need to sign in now
        if (!accessToken && passwordForLogin) {
          const finalEmail = email || `${usernameToUse.toLowerCase().replaceAll(' ', '_')}@kopmanpuzzel.nl`;
          const { error: sbError } = await supabase.auth.signInWithPassword({
            email: finalEmail,
            password: passwordForLogin
          });
          if (sbError) throw sbError;
        } else if (email && user) {
          // Manual email link during active session: Update state to hide the button immediately
          console.log("Email linked successfully via active session. Updating UI...");
          const updatedUser = { ...user, email: email };
          setUser(updatedUser);
          localStorage.setItem('kopmanpuzzel_user', JSON.stringify(updatedUser));

          // Force Supabase to refresh the session so handleSessionChange gets the new email
          await supabase.auth.refreshSession();
        }

        setShowMigrationModal(false);
        // If they linked an email, we are done. If they skipped (email is null),
        // keep the data so they can link later without re-entering password.
        if (email) {
          setMigrationPendingData(null);
          triggerToast("E-mailadres succesvol gekoppeld!");
        }
        setUsername('');
        setPassword('');
        ReactGA.event("migration_success");
      }
    } catch (err) {
      console.error(err);
      setMigrationError(err.response?.data?.error || "Migratie mislukt.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const identifier = username.trim();
    const mail = email.trim();

    if (!identifier || !mail || !password) {
      setRegisterError("Vul alsjeblieft alle velden in.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(mail)) {
      setRegisterError("Vul een geldig e-mailadres in.");
      return;
    }

    if (password.length < 6) {
      setRegisterError("Het wachtwoord moet minimaal 6 tekens lang zijn.");
      return;
    }

    setAuthLoading(true);
    isLocalAuthAction.current = true;
    setRegisterError(null);

    try {
      // 1. Register via Backend to handle duplicate usernames and unique proxy email generation
      const res = await axios.post(`${API_URL.trim()}/register`, {
        username: identifier,
        email: mail,
        password: password
      });

      if (res.data.status === 'SUCCESS') {
        // 2. Auto-login after registration
        // We use the email returned by the backend
        const { error: loginErr } = await supabase.auth.signInWithPassword({
          email: res.data.email,
          password: password
        });

        if (loginErr) throw loginErr;

        // Success
        ReactGA.event("register_success");
        setShowRegisterModal(false);
        setUsername('');
        setEmail('');
        setPassword('');
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || err.message || "Registratie mislukt.";
      setRegisterError(msg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    isLocalAuthAction.current = true;
    // 1. CLEAR SENSITIVE DATA ONLY
    clearSensitiveUserData();

    // 2. Clear state
    setUser(null);
    setFullCloudData({});
    lastSavedJson.current = "";
    lastLoadedMode.current = null;
    cloudStateHashes.current = {};
    cloudStateExists.current = {};
    cloudStateLoaded.current = {};
    gameMutationVersions.current = {};
    savedMutationVersions.current = {};

    // 3. Reset game state to defaults
    restoreGameState(getDefaultGameState(), false);

    // 4. Suppress immediate welcome message after logout for this session
    // It will reappear on refresh for guests (as requested)
    hasSeenWelcome.current = { klassiekers: true, giro: true, tour: true };
    hasSeenPuzzleWelcome.current = { klassiekers: true, giro: true, tour: true };

    // 5. Explicitly ensure modals are closed
    setShowLoginModal(false);
    setShowWelcomeMessage(false);
    setActiveTab('setup');
  };

  const switchToRegister = () => {
    setShowLoginModal(false);
    setLoginError(null);
    setShowRegisterModal(true);
    setRegisterError(null);
    setShowWelcomeMessage(false);
  };

  const switchToLogin = () => {
    setShowRegisterModal(false);
    setRegisterError(null);
    setShowResetPasswordModal(false);
    setResetError(null);
    setShowLoginModal(true);
    setLoginError(null);
    setShowWelcomeMessage(false);
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    const genericMessage = "Als dit emailadres bekend is ontvang je een mail om je wachtwoord aan te passen.";
    setAuthLoading(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      const normalizedApiUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      // 1. Rate-limit and validate the request through our backend without learning
      // whether the email exists. The backend intentionally returns no eligibility flag.
      try {
        await axios.post(`${normalizedApiUrl}/reset-email-check`, { email: resetEmail });
      } catch (checkErr) {
        if (checkErr.response && checkErr.response.status === 429) {
          throw new Error("Te veel verzoeken. Probeer het over een minuut opnieuw.");
        }
        throw new Error("Kan de server niet bereiken. Controleer je verbinding.");
      }

      // 2. Ask Supabase to send the reset email for normal email addresses. Proxy
      // @kopmanpuzzel.nl accounts do not have a real inbox, so those still get the
      // same generic success message without a Supabase call.
      const normalizedResetEmail = resetEmail.trim();
      const isProxyAccount = normalizedResetEmail.toLowerCase().endsWith('@kopmanpuzzel.nl');
      if (!isProxyAccount) {
        const { error } = await supabase.auth.resetPasswordForEmail(normalizedResetEmail, {
          redirectTo: `${window.location.origin}/`,
        });

        if (error) {
          const message = error.message?.toLowerCase() || '';
          if (!message.includes("not found") && error.status !== 404) {
            throw error;
          }
        }
      }

      // 3. ALWAYS show the generic success message
      setResetSuccess(genericMessage);
      setResetEmail('');
    } catch (err) {
      let finalMessage = err.message || "Er is iets misgegaan.";
      if (finalMessage.includes("security purposes") || finalMessage.includes("too many requests")) {
        finalMessage = "Wacht even af voordat je de mail opnieuw probeert te versturen.";
      }
      setResetError(finalMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setResetError(null);

    try {
      if (newPassword.length < 6) {
        throw new Error("Wachtwoord moet minimaal 6 tekens lang zijn.");
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setResetSuccess("Wachtwoord succesvol gewijzigd! Je bent nu ingelogd.");
      setNewPassword('');

      // Close modal after a short delay so the user can see the "beautiful" success message
      setTimeout(() => {
        setShowUpdatePasswordModal(false);
        setResetSuccess(null);
      }, 2500);
    } catch (err) {
      let finalMessage = err.message || "Kon wachtwoord niet bijwerken.";

      // Translate Supabase security error to Dutch
      if (finalMessage.includes("should be different from the old password")) {
        finalMessage = "Het nieuwe wachtwoord moet anders zijn dan het oude wachtwoord.";
      }

      setResetError(finalMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  // Load initial API data (Races/Riders)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Normalize API_URL by removing trailing slash if present
        const normalizedApiUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

        const [racesRes, ridersRes, competitionsRes, historicalRes] = await Promise.all([
          axios.get(`${normalizedApiUrl}/races?game=${gameMode}`),
          axios.get(`${normalizedApiUrl}/all-riders?game=${gameMode}`),
          axios.get(`${normalizedApiUrl}/competitions?game=${gameMode}`),
          axios.get(`${normalizedApiUrl}/historical-top-20?game=${gameMode}`).catch(err => {
            console.warn("Historical data not found (404), continuing with empty list");
            return { data: [] };
          })
        ]);

        if (gameModeRef.current !== gameMode) {
          console.log(`Discarding stale loadData fetch for ${gameMode} because active mode is now ${gameModeRef.current}`);
          return;
        }

        setRaces(racesRes.data);
        setRiders(ridersRes.data);
        setCompetitions(competitionsRes.data);
        setHistoricalData(historicalRes.data);
        dataGameMode.current = gameMode;

        // Initialize favorite riders structure
        // Use functional update to avoid overwriting restored state
        setFavoriteRiders(prev => {
          const next = { ...prev };
          racesRes.data.forEach(race => {
            if (!next[race]) {
              next[race] = [];
            }
          });
          return next;
        });

        setLoading(false);
      } catch (err) {
        console.error("Critical loading error:", err);
        setError('Fout bij het laden van gegevens: ' + err.message);
        setLoading(false);
      }
    };
    loadData();
  }, [gameMode]);

  // Show welcome message when setup tab is first visited
  useEffect(() => {
    if (!isHomePage && !isGameModeLoading && activeTab === 'setup' && !hasSeenWelcome.current[gameMode]) {
      const storageKey = `dontShowWelcomeAgain_${gameMode}`;
      const dontShowAgain = localStorage.getItem(storageKey);

      // Support conditional welcome for guests only AND check if they opted out
      if (!user && !dontShowAgain) {
        hasSeenWelcome.current[gameMode] = true;
        setDontShowAgainSetup(false); // Reset checkbox for the current game's modal
        setShowWelcomeMessage(true);
      }
    }
  }, [isHomePage, isGameModeLoading, activeTab, user, gameMode]);

  // Persistent tracking of previous tab and game mode for scroll control
  const prevTabRef = useRef(activeTab);
  const prevGameModeRef = useRef(gameMode);

  // Show welcome message when puzzle tab is first visited
  useEffect(() => {
    if (!isHomePage && activeTab === 'puzzle') {
      // Only scroll to top if we actually just switched to this tab or changed the game
      if (prevTabRef.current !== 'puzzle' || prevGameModeRef.current !== gameMode) {
        window.scrollTo(0, 0);
      }

      if (!hasSeenPuzzleWelcome.current[gameMode]) {
        // Step 2 welcome logic...
        const setupStorageKey = `dontShowWelcomeAgain_${gameMode}`;
        const setupDontShowAgain = localStorage.getItem(setupStorageKey);

        if (!user && !setupDontShowAgain && hasSeenWelcome.current[gameMode]) {
          hasSeenPuzzleWelcome.current[gameMode] = true;
          setShowPuzzleWelcomeMessage(true);
        }
      }
    }
    prevTabRef.current = activeTab;
    prevGameModeRef.current = gameMode;
  }, [isHomePage, activeTab, user, gameMode]);

  const handleSetFavorite = useCallback((race, rider, rank) => {
    markGameModified();
    // rank: 0 means remove
    setFavoriteRiders(prev => {
      const next = { ...prev };
      const trimmedRider = rider.trim();
      const list = (next[race] || []).filter(f => f.rider.trim() !== trimmedRider);
      if (rank > 0) {
        list.push({ rider: trimmedRider, rank: parseInt(rank, 10) });
      }
      // sort by rank ascending (1 highest)
      list.sort((a, b) => a.rank - b.rank);
      next[race] = list;
      return next;
    });
  }, [markGameModified]);

  const handleSetMultipleFavorites = useCallback((race, updates) => {
    markGameModified();
    // updates: [{rider, rank}]
    setFavoriteRiders(prev => {
      const next = { ...prev };
      let list = (next[race] || []).map(f => ({ ...f }));

      updates.forEach(update => {
        const trimmedRider = update.rider.trim();
        list = list.filter(f => f.rider.trim() !== trimmedRider);
        if (update.rank > 0) {
          list.push({ rider: trimmedRider, rank: parseInt(update.rank, 10) });
        }
      });

      list.sort((a, b) => a.rank - b.rank);
      next[race] = list;
      return next;
    });
  }, [markGameModified]);

  const handleCopyFavorites = useCallback((targetRace, sourceRace) => {
    markGameModified();
    setFavoriteRiders(prev => {
      const sourceFavs = prev[sourceRace] || [];
      return {
        ...prev,
        [targetRace]: sourceFavs.map(f => ({ ...f }))
      };
    });
  }, [markGameModified]);

  const handleShowTop20 = useCallback((name, raceId) => {
    ReactGA.event("view_race_top20", {
      item: name
    });
    setModalStack(prev => [...prev, { type: 'top20', data: { name, raceId }, id: Date.now() + Math.random() }]);
  }, []);

  const handleShowOverview = useCallback(() => {
    ReactGA.event("view_rider_overview");
    setModalStack(prev => [...prev, { type: 'overview', data: {}, id: Date.now() + Math.random() }]);
  }, []);

  const handleShowHistory = useCallback((riderObj) => {
    ReactGA.event("view_rider_history", {
      item: riderObj.name
    });

    // Ensure we have the latest quality data from the main riders state
    const latestRiderData = riders[riderObj.name] || {};
    const fullRiderObj = { ...latestRiderData, ...riderObj };

    setModalStack(prev => {
      const historyItems = prev.filter(m => m.type === 'history');
      if (historyItems.length >= 10) {
        return prev;
      }
      return [...prev, { type: 'history', data: fullRiderObj, id: Date.now() + Math.random() }];
    });
  }, [riders]);

  const handleShowRiderHistoryByName = useCallback((name) => {
    const riderData = riders[name];
    if (riderData) {
      handleShowHistory({
        name,
        riderId: riderData.RiderId,
        team_abbr: riderData.team_abbr,
        klassement: riderData.klassement || 0,
        klimmen: riderData.klimmen || 0,
        tijdrijden: riderData.tijdrijden || 0,
        sprinten: riderData.sprinten || 0,
        punchen: riderData.punchen || 0,
        heuvels: riderData.heuvels || 0,
        kasseien: riderData.kasseien || 0
      });
    } else {
      console.warn(`Rider ${name} not found in database`);
    }
  }, [riders, handleShowHistory]);

  const handleCloseModal = (modalId, event = null) => {
    if (!event) {
      setModalStack(prev => prev.filter(m => m.id !== modalId));
      return;
    }

    // Hierarchical click logic:
    // 1. If we click 'beside' the current top modal, we close ONLY the top modal.
    // 2. BUT if that click point is ALSO 'beside' all other modals in the stack, we close EVERYTHING.
    // We determine this by checking the bounding rects of all modal contents.

    const clickX = event.clientX;
    const clickY = event.clientY;

    setModalStack(prev => {
      if (prev.length === 0) return prev;

      // Find all DOM elements representing modal boxes
      const modalBoxes = Array.from(document.querySelectorAll('.modal-box'));

      // Find which modal models are 'hit' by the click
      const hitModalIds = modalBoxes
        .filter(box => {
          const rect = box.getBoundingClientRect();
          return (
            clickX >= rect.left &&
            clickX <= rect.right &&
            clickY >= rect.top &&
            clickY <= rect.bottom
          );
        })
        .map(box => box.getAttribute('data-modal-id'));

      if (hitModalIds.length === 0) {
        // Clicked completely outside: close everything
        return [];
      } else {
        // Find the index of the highest modal in the stack that was clicked
        let highestIndex = -1;
        hitModalIds.forEach(id => {
          const idx = prev.findIndex(m => String(m.id) === String(id));
          if (idx > highestIndex) highestIndex = idx;
        });

        if (highestIndex === -1) {
          // Fallback if IDs don't match for some reason
          return prev.filter(m => m.id !== modalId);
        }

        // Keep all modals up to (including) the highest clicked one
        return prev.slice(0, highestIndex + 1);
      }
    });
  };

  const handleRemoveFavorite = useCallback((race, rider, isGlobal = false) => {
    markGameModified();
    setFavoriteRiders(prev => {
      const next = { ...prev };

      const removeAndRerank = (list, rToRemove) => {
        const filtered = (list || []).filter(f => f.rider !== rToRemove);
        // Important: Re-rank sequentially to ensure no gaps (e.g. #1, #2, #3...)
        return filtered
          .sort((a, b) => a.rank - b.rank)
          .map((f, idx) => ({ ...f, rank: idx + 1 }));
      };

      if (isGlobal && isGrandTourMode(gameMode)) {
        // Remove from ALL races in Giro mode
        Object.keys(next).forEach(rName => {
          next[rName] = removeAndRerank(next[rName], rider);
        });
      } else {
        // Default: remove only from current race
        next[race] = removeAndRerank(next[race], rider);
      }
      return next;
    });
  }, [gameMode, markGameModified]);

  const handleToggleRider = (rider) => {
    markGameModified();
    setConfigurations(prev =>
      prev.map(config =>
        config.id === activeConfigId
          ? {
            ...config,
            selectedRiders: config.selectedRiders.includes(rider)
              ? config.selectedRiders.filter(r => r !== rider)
              : [...config.selectedRiders, rider]
          }
          : config
      )
    );
  };

  const handleAddConfiguration = () => {
    markGameModified();
    const newConfigs = [...configurations, {
      id: configurations.length + 1,
      selectedRiders: [],
      plannedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
      selectedSwapAfterRace: { '0': null, '1': null, '2': null },
      appliedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
      appliedSelectedSwapAfterRace: { '0': null, '1': null, '2': null },
      name: ''
    }];
    // Renumber all configurations
    const renumbered = newConfigs.map((config, index) => ({
      ...config,
      id: index + 1
    }));
    setConfigurations(renumbered);
    setActiveConfigId(renumbered.length);
  };

  const handleUpdateConfigName = (configId, newName) => {
    markGameModified();
    setConfigurations(prev =>
      prev.map(config =>
        config.id === configId ? { ...config, name: newName } : config
      )
    );
  };

  const handleSwitchConfiguration = (configId) => {
    markGameModified();
    setActiveConfigId(configId);
  };

  const handleDeleteConfiguration = (configId) => {
    // Don't allow deleting if it's the only configuration
    if (configurations.length <= 1) return;

    // Find the configuration to check if it has riders
    const configToDelete = configurations.find(c => c.id === configId);

    // If the configuration is empty, delete it directly
    if (!configToDelete || configToDelete.selectedRiders.length === 0) {
      performDeleteConfiguration(configId);
      return;
    }

    // If it has riders, show confirmation dialog
    setDeleteConfigId(configId);
    setShowDeleteConfirm(true);
  };

  const performDeleteConfiguration = (configId) => {
    markGameModified();
    // Find the index of the currently active config
    const activeConfigIndex = configurations.findIndex(c => c.id === activeConfigId);

    // Find the index of the config to delete
    const deleteIndex = configurations.findIndex(c => c.id === configId);

    // Filter out the deleted config
    const filtered = configurations.filter(config => config.id !== configId);

    // Renumber all remaining configurations
    const renumbered = filtered.map((config, index) => ({
      ...config,
      id: index + 1
    }));

    setConfigurations(renumbered);

    // Calculate the new active index
    let newActiveIndex = activeConfigIndex;

    // If we deleted a config before the active config, the active config's index shifts down by 1
    if (deleteIndex < activeConfigIndex) {
      newActiveIndex = activeConfigIndex - 1;
    }

    // If the active config itself was deleted, go to the previous config (if exists)
    if (configId === activeConfigId) {
      if (activeConfigIndex > 0) {
        newActiveIndex = activeConfigIndex - 1;
      } else {
        newActiveIndex = 0; // If it was the first one, stay at position 0 (which is now the next config)
      }
    }

    // Set the new active ID based on the new index
    if (newActiveIndex >= 0 && newActiveIndex < renumbered.length) {
      setActiveConfigId(renumbered[newActiveIndex].id);
    } else if (renumbered.length > 0) {
      setActiveConfigId(renumbered[0].id);
    }
  };

  const confirmDeleteConfiguration = () => {
    performDeleteConfiguration(deleteConfigId);
    setShowDeleteConfirm(false);
    setDeleteConfigId(null);
  };

  const cancelDeleteConfiguration = () => {
    setShowDeleteConfirm(false);
    setDeleteConfigId(null);
  };

  const calculateSwapDemand = (swaps, swapRaces, configRiders) => {
    if (!swaps || !swapRaces) return 0;
    const validSwaps = [];
    swaps.forEach((swap, index) => {
      if (swap.out && swap.in && swapRaces[index]) {
        const raceIndex = races.indexOf(swapRaces[index]);
        if (raceIndex === -1) return; // Skip if not found
        const outRiderInfo = riders[swap.out];
        const outPrice = typeof outRiderInfo === 'object' ? outRiderInfo.price : (outRiderInfo || 500000);
        const inRiderInfo = riders[swap.in];
        const inPrice = typeof inRiderInfo === 'object' ? inRiderInfo.price : (inRiderInfo || 500000);
        validSwaps.push({ index, raceIndex, outPrice, inPrice });
      }
    });

    // Group by raceIndex
    const raceCosts = {};
    validSwaps.forEach(swap => {
      if (!raceCosts[swap.raceIndex]) {
        raceCosts[swap.raceIndex] = 0;
      }
      raceCosts[swap.raceIndex] += (swap.inPrice - swap.outPrice);
    });

    // Sort race indices chronologically
    const sortedRaces = Object.keys(raceCosts).map(Number).sort((a, b) => a - b);

    let swapPot = 0;
    let swapDemand = 0;

    sortedRaces.forEach(raceIdx => {
      const cost = raceCosts[raceIdx];
      if (cost < 0) {
        swapPot += Math.abs(cost);
      } else if (cost > 0) {
        const paidFromPot = Math.min(cost, swapPot);
        swapPot -= paidFromPot;
        const remainingCost = cost - paidFromPot;
        swapDemand += remainingCost;
      }
    });

    return swapDemand;
  };

  const isConfigurationOverBudget = (config) => {
    const spentBudget = config.selectedRiders.reduce((sum, rider) => {
      const riderInfo = riders[rider];
      const price = typeof riderInfo === 'object' ? riderInfo.price : (riderInfo || 500000);
      return sum + price;
    }, 0);

    const swapDemand = calculateSwapDemand(config.appliedSwaps, config.appliedSelectedSwapAfterRace, config.selectedRiders);

    let minPossibleTotal;
    if (config.selectedRiders.length > 20) {
      minPossibleTotal = spentBudget + swapDemand;
    } else {
      minPossibleTotal = spentBudget + Math.max(0, 20 - config.selectedRiders.length) * 500000 + swapDemand;
    }
    return minPossibleTotal > totalBudget || config.selectedRiders.length > 20;
  };


  const handleReset = () => {
    // Only show reset confirmation if the current configuration has selected riders
    if (activeConfig?.selectedRiders && activeConfig.selectedRiders.length > 0) {
      setShowResetConfirm(true);
    }
  };

  const handleOpenDropdown = useCallback((race) => {
    setOpenDropdownRace(prev => prev === race ? null : race);
  }, []);

  const handleOpenNotes = useCallback((rider) => {
    setNotesTarget(rider);
    setShowNotesModal(true);
  }, []);

  const confirmReset = () => {
    markGameModified();
    setConfigurations(prev =>
      prev.map(config =>
        config.id === activeConfigId
          ? {
            ...config,
            selectedRiders: [],
            plannedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
            selectedSwapAfterRace: { '0': null, '1': null, '2': null },
            appliedSwaps: [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }],
            appliedSelectedSwapAfterRace: { '0': null, '1': null, '2': null }
          }
          : config
      )
    );
    setShowResetConfirm(false);
  };

  const cancelReset = () => {
    setShowResetConfirm(false);
  };

  // Update swaps for current configuration
  const handleUpdateSwaps = (plannedSwaps, selectedSwapAfterRace, appliedSwaps, appliedSelectedSwapAfterRace) => {
    markGameModified();
    setConfigurations(prev =>
      prev.map(config =>
        config.id === activeConfigId
          ? {
            ...config,
            plannedSwaps,
            selectedSwapAfterRace,
            appliedSwaps,
            appliedSelectedSwapAfterRace
          }
          : config
      )
    );
  };

  // Calculate maximum rank count across all races for consistent table heights
  const maxFavoritesRankPerRace = Math.max(
    ...races.map(race => (favoriteRiders[race] || []).length || 0),
    1
  );

  // Merge Custom Riders into main Riders object for Step 2 (Puzzle)
  // This ensures they appear as a single rider with multiple races
  const augmentedRiders = React.useMemo(() => {
    // Deep copy to avoid mutating original state
    if (Object.keys(riders).length === 0) return {};

    const newRiders = JSON.parse(JSON.stringify(riders));

    // 1. Add Custom Riders
    customRiders.forEach(({ rider, race }) => {
      if (newRiders[rider]) {
        if (!newRiders[rider].races) newRiders[rider].races = [];
        if (!newRiders[rider].races.includes(race)) {
          newRiders[rider].races.push(race);
        }
      }
    });

    return newRiders;
  }, [riders, customRiders]);

  // Sync selected riders with available riders (augmentedRiders)
  // Removes riders from selection if they no longer exist or have no races
  useEffect(() => {
    if (loading || dataGameMode.current !== gameMode) return;

    setConfigurations(prevConfigs => {
      let hasChanges = false;
      const newConfigs = prevConfigs.map(config => {
        const validSelectedRiders = config.selectedRiders.filter(riderId => {
          const riderData = augmentedRiders[riderId];
          return riderData && riderData.races && riderData.races.length > 0;
        });

        // CHRONOLOGICAL UNIFIED SIMULATION
        // 1. Collect ALL swaps. If a swap is in both Applied and Planned, MERGE them.
        // 2. Sort by Race Order.
        // 3. Simulate sequentially with Reserve Budget Check.
        // 4. Mark invalid swaps for removal.



        // Arrays to hold the *final* valid state
        const validAppliedSwaps = [...config.appliedSwaps];
        const validAppliedSelectedSwapAfterRace = { ...config.appliedSelectedSwapAfterRace };
        const validPlannedSwaps = [...config.plannedSwaps];
        const validSelectedSwapAfterRace = { ...config.selectedSwapAfterRace };

        // Helper to reset a swap
        const resetSwap = (type, index) => {
          if (type === 'applied' || type === 'merged') {
            validAppliedSwaps[index] = { out: '', in: '' };
            if (validAppliedSelectedSwapAfterRace[index.toString()]) {
              validAppliedSelectedSwapAfterRace[index.toString()] = null;
            }
          }
          if (type === 'planned' || type === 'merged') {
            validPlannedSwaps[index] = { out: '', in: '' };
            if (validSelectedSwapAfterRace[index.toString()]) {
              validSelectedSwapAfterRace[index.toString()] = null;
            }
          }
        };

        // 1. Build Simulation List with Deduplication
        let simulationSwaps = [];

        // Add Applied Swaps first
        config.appliedSwaps.forEach((swap, idx) => {
          if (swap.out && swap.in) {
            const raceName = config.appliedSelectedSwapAfterRace[idx.toString()];
            const raceIdx = raceName ? races.indexOf(raceName) : -1;

            if (raceIdx !== -1) {
              simulationSwaps.push({
                ...swap,
                type: 'applied',
                originalIndex: idx,
                raceIdx,
                key: `${swap.out}-${swap.in}-${raceIdx}`
              });
            } else {
              resetSwap('applied', idx);
            }
          }
        });

        // Add Planned Swaps (Merge if identical to Applied)
        config.plannedSwaps.forEach((swap, idx) => {
          if (swap.out && swap.in) {
            const raceName = config.selectedSwapAfterRace[idx.toString()];
            const raceIdx = raceName ? races.indexOf(raceName) : -1;

            if (raceIdx !== -1) {
              const key = `${swap.out}-${swap.in}-${raceIdx}`;
              const existing = simulationSwaps.find(s => s.key === key && s.type === 'applied'); // Only merge with applied

              if (existing) {
                existing.type = 'merged';
                existing.plannedIndex = idx;
              } else {
                simulationSwaps.push({
                  ...swap,
                  type: 'planned',
                  originalIndex: idx,
                  raceIdx,
                  key
                });
              }
            } else {
              resetSwap('planned', idx);
            }
          }
        });

        // Update resetSwap logic for merged
        const resetSwapMerged = (simSwap) => {
          if (simSwap.type === 'applied') {
            resetSwap('applied', simSwap.originalIndex);
          } else if (simSwap.type === 'planned') {
            resetSwap('planned', simSwap.originalIndex);
          } else if (simSwap.type === 'merged') {
            resetSwap('applied', simSwap.originalIndex);
            resetSwap('planned', simSwap.plannedIndex);
          }
        };

        // 2. Sort Chronologically
        simulationSwaps.sort((a, b) => {
          if (a.raceIdx !== b.raceIdx) return a.raceIdx - b.raceIdx;
          return a.originalIndex - b.originalIndex;
        });

        // 3. Run Simulation
        let currentSimulationTeam = [...validSelectedRiders];

        simulationSwaps.forEach(simSwap => {
          const inRiderData = augmentedRiders[simSwap.in];
          const outRiderData = augmentedRiders[simSwap.out];

          // A. Basic Validity: riders must exist
          const isOutValid = simSwap.out && outRiderData;
          const isInValid = simSwap.in && inRiderData && inRiderData.races && inRiderData.races.length > 0;

          // B. Chain Validity: out rider must be in the team AT THAT MOMENT
          const isOutInTeam = currentSimulationTeam.includes(simSwap.out);

          // NOTE: We no longer perform Strict Budget Validity (Broekzak-Vestzak) here,
          // because the user wants to allow all swaps as long as they are structurally valid.
          // Budget overages are handled by calculateSwapDemand elsewhere.

          if (isOutValid && isInValid && isOutInTeam) {
            // Valid: Execute the swap in the simulation to track who is in the team for later swaps
            currentSimulationTeam = currentSimulationTeam.filter(r => r !== simSwap.out);
            currentSimulationTeam.push(simSwap.in);
          } else {
            // Invalid: Reset based on type (e.g. if rider was manually deselected)
            resetSwapMerged(simSwap);
          }
        });

        // Check if anything changed
        const selectedChanged = validSelectedRiders.length !== config.selectedRiders.length;
        const plannedChanged = JSON.stringify(validPlannedSwaps) !== JSON.stringify(config.plannedSwaps);
        const appliedChanged = JSON.stringify(validAppliedSwaps) !== JSON.stringify(config.appliedSwaps);
        const metadataChanged = JSON.stringify(validSelectedSwapAfterRace) !== JSON.stringify(config.selectedSwapAfterRace) ||
          JSON.stringify(validAppliedSelectedSwapAfterRace) !== JSON.stringify(config.appliedSelectedSwapAfterRace);

        if (selectedChanged || plannedChanged || appliedChanged || metadataChanged) {
          hasChanges = true;
          return {
            ...config,
            selectedRiders: validSelectedRiders,
            plannedSwaps: validPlannedSwaps,
            selectedSwapAfterRace: validSelectedSwapAfterRace,
            appliedSwaps: validAppliedSwaps,
            appliedSelectedSwapAfterRace: validAppliedSelectedSwapAfterRace
          };
        }
        return config;
      });

      return hasChanges ? newConfigs : prevConfigs;
    });
  }, [augmentedRiders, loading, races, gameMode]);

  useEffect(() => {
    const handlePopState = () => {
      const routeMode = getRouteGameMode();

      if (!routeMode) {
        setIsHomePage(isHomeRoute());
        return;
      }

      setIsHomePage(false);
      if (routeMode !== gameModeRef.current) {
        handleGameModeChange({ target: { value: routeMode } });
      } else {
        setActiveTab('setup');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  });
  if (error) {
    return <div className="app-error">{error}</div>;
  }





  const handleAttemptSwitchToPuzzle = () => {
    if (hasInvalidFavorites) {
      setShowInvalidRiderWarning(true);
    } else {
      setActiveTab('puzzle');
    }
  };

  const confirmSwitchToPuzzle = () => {
    setShowInvalidRiderWarning(false);
    setActiveTab('puzzle');
  };

  const cancelSwitchToPuzzle = () => {
    setShowInvalidRiderWarning(false);
  };



  const handleGameModeChange = (e) => {
    const newMode = e.target.value;
    const nextPath = GAME_MODE_PATHS[newMode];
    if (typeof window !== 'undefined' && nextPath && window.location.pathname !== nextPath) {
      window.history.pushState(null, '', nextPath);
    }
    setIsHomePage(false);
    if (newMode === gameMode) {
      setActiveTab('setup');
      return;
    }
    const currentModeReady = lastLoadedMode.current === gameMode && dataGameMode.current === gameMode;

    // 0. Hide the old mode immediately and block auto-saves until both data sources are ready.
    setLoading(true);
    setError(null);
    dataGameMode.current = null;
    isRestoring.current = true;
    lastLoadedMode.current = null;

    // 1. Manually trigger a save of the CURRENT game's memory state before switching
    const storage = user ? localStorage : sessionStorage;
    const multiData = getMultiGameState();
    const currentGameState = getGameStateObject();
    storage.setItem('kopmanpuzzel_multi_state', JSON.stringify(multiData));

    if (user) {
      // Keep the in-memory account cache current only when the current mode is fully loaded.
      // During login/account restore this prevents an empty visible state from replacing the
      // already fetched account state in memory.
      if (currentModeReady) {
        setFullCloudData(prev => ({ ...prev, [gameMode]: currentGameState }));
        save_game_state(multiData, gameMode, currentGameState);
      }
    }

    // Always save to localStorage for the game mode setting to survive refreshes/reboots
    localStorage.setItem('kopmanpuzzel_last_mode', newMode);

    // 2. Perform the switch
    setGameMode(newMode);
    setActiveTab('setup');

    // 3. Logged-in users always use account data. Guest users use session data.
    if (user) {
      if (Object.prototype.hasOwnProperty.call(fullCloudData, newMode)) {
        console.log(`Restoring ${newMode} from account cache`);
        restoreGameState(fullCloudData[newMode] || {}, true, newMode);
      } else {
        console.log(`Fetching ${newMode} from cloud...`);
        syncGameWithMigration(user.access_token, newMode, null, null);
      }
    } else if (multiData.games && multiData.games[newMode] && !isGameDataEmpty(multiData.games[newMode])) {
      console.log(`Restoring ${newMode} from guest session`);
      restoreGameState(multiData.games[newMode], false, newMode);
    } else {
      console.log(`Starting ${newMode} fresh`);
      restoreGameState(getDefaultGameState(), false, newMode);
    }

    // Reset transient transient states
    setSortBy('price');
    setFilterFavorites(false);
    setSelectedRaces([]);
    setOpenDropdownRace(null);
    setModalStack([]);

    // For Giro, always default to 'favorites' view as 'coverage' is redundant
    if (isGrandTourMode(newMode)) {
      setTableView('favorites');
    }
  };

  const goToHomePage = () => {
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.history.pushState(null, '', '/');
    }
    setIsHomePage(true);
  };

  const navigateToGameMode = (mode) => {
    if (!VALID_GAME_MODES.includes(mode)) return;

    const nextPath = GAME_MODE_PATHS[mode];
    if (typeof window !== 'undefined' && nextPath && window.location.pathname !== nextPath) {
      window.history.pushState(null, '', nextPath);
    }

    setIsHomePage(false);

    if (mode === gameMode) {
      setActiveTab('setup');
      return;
    }

    handleGameModeChange({ target: { value: mode } });
  };

  const authModals = (
    <>
      {showLoginModal && (
        <div className="delete-confirm-overlay" onClick={() => {
          setShowLoginModal(false);
          setLoginError(null);
        }}>
          <div className="delete-confirm-modal auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="welcome-close-btn" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                setShowLoginModal(false);
                setLoginError(null);
              }, 50);
            }}>&times;</button>
            <h2>Inloggen</h2>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
              Vul je e-mailadres of je gebruikersnaam in om verder te gaan.
            </p>

            {loginError && (
              <div style={{
                backgroundColor: '#fff5f5',
                color: '#c53030',
                padding: '0.5rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.8rem',
                marginBottom: '1rem',
                border: '1px solid #feb2b2',
                textAlign: 'left'
              }}>
                {loginError}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="login-username">E-mail of Gebruikersnaam</label>
                <input
                  id="login-username"
                  name="username"
                  autoComplete="username"
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (loginError) setLoginError(null);
                  }}
                  placeholder="jouw@email.nl of gebruikersnaam"
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label htmlFor="login-password">Wachtwoord</label>
                <input
                  id="login-password"
                  name="password"
                  autoComplete="current-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (loginError) setLoginError(null);
                  }}
                  placeholder="Wachtwoord"
                  maxLength={100}
                />
              </div>
              <button type="submit" className="submit-button" style={{ width: '100%', marginTop: '1rem' }}>
                Inloggen
              </button>
            </form>
            <div style={{ marginTop: '1.25rem' }}>
              <p style={{ margin: '0', fontSize: '0.9rem' }}>
                Nog geen account? <button className="link-button" onClick={switchToRegister}>Maak account aan</button>
              </p>
              <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.9rem' }}>
                Wachtwoord vergeten? <button className="link-button" onClick={() => {
                  setShowLoginModal(false);
                  setShowResetPasswordModal(true);
                }}>Maak een nieuw wachtwoord aan</button>
              </p>
            </div>
          </div>
        </div>
      )}

      {showRegisterModal && (
        <div className="delete-confirm-overlay" onClick={() => {
          setShowRegisterModal(false);
          setRegisterError(null);
        }}>
          <div className="delete-confirm-modal auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="welcome-close-btn" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                setShowRegisterModal(false);
                setRegisterError(null);
              }, 50);
            }}>&times;</button>
            <h2>Account aanmaken</h2>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
              Vul een e-mailadres en een gebruikersnaam in om een account aan te maken.
            </p>

            {registerError && (
              <div style={{
                backgroundColor: '#fff5f5',
                color: '#c53030',
                padding: '0.5rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.8rem',
                marginBottom: '1rem',
                border: '1px solid #feb2b2',
                textAlign: 'left'
              }}>
                {registerError}
              </div>
            )}

            <form onSubmit={handleRegister} noValidate>
              <div className="form-group">
                <label htmlFor="register-email">
                  E-mailadres:
                  <span className="info-icon-container">
                    <span className="info-icon">i</span>
                    <span className="info-tooltip">
                      Koppel een e-mailadres om je account te beveiligen en makkelijker in te loggen in de toekomst.
                      <br /><br />
                      Geen zorgen, we gebruiken je e-mail alleen voor beveiliging en accountbeheer.
                    </span>
                  </span>
                </label>
                <input
                  id="register-email"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (registerError) setRegisterError(null);
                  }}
                  placeholder="jouw@email.nl"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="register-username">Gebruikersnaam:</label>
                <input
                  id="register-username"
                  name="username"
                  autoComplete="username"
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (registerError) setRegisterError(null);
                  }}
                  placeholder="Gebruikersnaam"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="register-password">Kies een wachtwoord:</label>
                <input
                  id="register-password"
                  name="password"
                  autoComplete="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (registerError) setRegisterError(null);
                  }}
                  placeholder="Wachtwoord"
                  required
                />
              </div>
              <button type="submit" className="submit-button success" style={{ width: '100%', marginTop: '1rem' }} disabled={authLoading}>
                {authLoading ? 'Laden...' : 'Account aanmaken'}
              </button>
            </form>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
              Heb je al een account? <button className="link-button" onClick={switchToLogin}>Log in</button>
            </p>
          </div>
        </div>
      )}

      {showMigrationModal && (
        <EmailLinkModal
          username={migrationPendingData?.username || user?.name}
          isLoading={authLoading}
          externalError={migrationError}
          onMigrate={handleMigrate}
          onSkip={() => {
            setShowMigrationModal(false);
            setMigrationPendingData(null);
            setMigrationError(null);
          }}
        />
      )}

      {showResetPasswordModal && (
        <div className="delete-confirm-overlay" onClick={() => setShowResetPasswordModal(false)}>
          <div className="delete-confirm-modal auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="welcome-close-btn" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => setShowResetPasswordModal(false), 50);
            }}>&times;</button>
            <h2>Wachtwoord vergeten</h2>
            <p className="auth-instruction" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
              Vul je e-mailadres in om een herstellink te ontvangen.
            </p>
            {resetError && <div className="error-message" style={{ color: '#c53030', backgroundColor: '#fff5f5', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.8rem' }}>{resetError}</div>}
            {resetSuccess && <div className="success-message" style={{ color: '#2f855a', backgroundColor: '#f0fff4', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.8rem' }}>{resetSuccess}</div>}
            <form onSubmit={handleResetRequest}>
              <div className="form-group">
                <label>E-mailadres</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="jouw@email.nl"
                  maxLength={255}
                  required
                />
              </div>
              <button disabled={authLoading} className="submit-button success" type="submit" style={{ width: '100%', marginTop: '1rem' }}>
                {authLoading ? 'Verzenden...' : 'Herstellink verzenden'}
              </button>
            </form>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
              Terug naar <button className="link-button" onClick={switchToLogin}>Inloggen</button>
            </p>
          </div>
        </div>
      )}

      {showUpdatePasswordModal && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal auth-modal">
            <button className="welcome-close-btn" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => setShowUpdatePasswordModal(false), 50);
            }}>&times;</button>
            <h2>Nieuw wachtwoord instellen</h2>
            <p className="auth-instruction" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
              Kies een veilig nieuw wachtwoord voor je account.
            </p>
            {resetError && <div className="error-message" style={{ color: '#c53030', backgroundColor: '#fff5f5', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.8rem' }}>{resetError}</div>}
            {resetSuccess && <div className="success-message" style={{ color: '#2f855a', backgroundColor: '#f0fff4', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.8rem' }}>{resetSuccess}</div>}
            <form onSubmit={handleUpdatePassword}>
              <div className="form-group">
                <label>Nieuw Wachtwoord</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  maxLength={100}
                  required
                />
              </div>
              <button disabled={authLoading} className="submit-button success" type="submit" style={{ width: '100%', marginTop: '1rem' }}>
                {authLoading ? 'Bezig...' : 'Wachtwoord bijwerken'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showSessionMismatchModal && (
        <div className="delete-confirm-overlay session-mismatch-overlay">
          <div className="delete-confirm-modal auth-modal session-mismatch-modal">
            <h2>Gebruiker gewijzigd</h2>
            <p className="auth-instruction" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
              Er is gedetecteerd dat er in een ander tabblad is ingelogd (of uitgelogd) met een ander account.
            </p>
            <div className="warning-content" style={{ backgroundColor: '#fff5f5', color: '#c53030', padding: '1rem', borderRadius: '8px', border: '1px solid #feb2b2', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Om te voorkomen dat je gegevens overschrijft van het verkeerde account, is dit scherm tijdelijk geblokkeerd.
            </div>
            <button
              className="submit-button"
              onClick={() => window.location.reload()}
              style={{ width: '100%' }}
            >
              Pagina vernieuwen
            </button>
          </div>
        </div>
      )}


    </>
  );


  if (isHomePage) {
    const gameCards = HOME_GAME_OPTIONS.map(({ mode, title, description }) => ({
      mode,
      name: title,
      desc: description,
      cta: mode === 'tour' ? 'Spelen →' : 'Terugkijken →'
    }));

    return (
      <div className="app home-page">
        <main>
          <div className="header-auth">
            {user ? (
              <div
                className={`user-profile ${showUserMenu ? 'menu-open' : ''}`}
                ref={userProfileRef}
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <span className="user-name">{user.name}</span>
                <div className="user-actions-dropdown" onClick={(e) => e.stopPropagation()}>
                  {user.email && !user.email.toLowerCase().endsWith('@kopmanpuzzel.nl') && (
                    <div className="user-email-box">{user.email}</div>
                  )}
                  {user.email?.toLowerCase().endsWith('@kopmanpuzzel.nl') && (
                    <button className="link-email-btn" onClick={() => setShowMigrationModal(true)}>
                      Koppel E-mail
                    </button>
                  )}
                  <button className="logout-btn" onClick={handleLogout}>Uitloggen</button>
                </div>
              </div>
            ) : (
              <button className="login-btn" onClick={() => setShowLoginModal(true)}>Inloggen</button>
            )}
          </div>

          <section className="home-hero" aria-labelledby="home-title">
            <h1 id="home-title" className="home-hero-title">Kopmanpuzzel</h1>
            <p className="home-hero-subtitle">Jouw wielerspel puzzelhulp</p>
            <p className="home-hero-description">
              {seoPages.home.visibleDescription}
            </p>
            <p className="home-hero-tagline-bottom">Puzzelen was nog nooit zo makkelijk.</p>
          </section>

          <section className="home-section" aria-labelledby="home-games-title">
            <h2 id="home-games-title" className="home-section-title">Kies een spelmodus om te beginnen</h2>
            <div className="home-grid">
              {gameCards.map(({ mode, name, desc, cta }) => {
                const summary = homeGameSummaries[mode] || {};
                return (
                  <button
                    key={mode}
                    className={`home-game-card ${mode}`}
                    onClick={() => navigateToGameMode(mode)}
                  >
                    <div className="home-card-top" />
                    <div className="home-card-content">
                      <h3 className="home-card-name">{name}</h3>
                      <p className="home-card-text">{desc}</p>
                      {user && (
                        <div className="home-card-stats" aria-label={`${name} voortgang`}>
                          <div className="home-card-stat">
                            <strong>{summary.isLoading ? '...' : (summary.riderCount ?? 0)}</strong>
                            <small>renners</small>
                          </div>
                          <div className={`home-card-stat ${summary.isOverBudget ? 'over-budget' : ''}`}>
                            <strong>{summary.isLoading ? '...' : formatBudgetShort(summary.spent || 0)}</strong>
                            <small>van {formatBudgetShort(getBudgetLimitForMode(mode))}</small>
                          </div>
                        </div>
                      )}
                      <span className="home-card-footer">{cta}</span>
                    </div>
                  </button>
                );
              })}
              <button className="home-game-card vuelta" disabled aria-disabled="true">
                <div className="home-card-top" />
                <div className="home-card-content">
                  <h3 className="home-card-name">Vuelta</h3>
                  <p className="home-card-text">Later beschikbaar.</p>
                  <span className="home-card-footer">Binnenkort</span>
                </div>
              </button>
            </div>
          </section>
        </main>
        {authModals}
      </div>
    );
  }
  return (
    <div className="app">
      <header className="app-header">
        <button className="header-title-button" type="button" onClick={goToHomePage}>{gameMode === 'klassiekers' ? 'Kopmanpuzzel' : `Kopmanpuzzel ${grandTourShortName}`}</button>
        <p>
          {isGrandTour
            ? 'Selecteer je favoriete renners per etappe en stel je team samen'
            : 'Selecteer je favoriete kopmannen per wedstrijd en stel je team samen'
          }
          {isGrandTour && <span className={`${gameMode}-dot`}></span>}
        </p>
        <div className="header-controls">
          <HeaderGameSelector
            gameMode={gameMode}
            onGameModeChange={handleGameModeChange}
          />
          <button
            className="notes-trigger-btn"
            onClick={() => {
              setNotesTarget('algemeen');
              setShowNotesModal(true);
            }}
            title="Notities openen"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            <span>Notities</span>
          </button>
        </div>


        <div className="header-auth">
          {user ? (
            <div
              className={`user-profile ${showUserMenu ? 'menu-open' : ''}`}
              ref={userProfileRef}
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <span className="user-name">{user.name}</span>
              <div className="user-actions-dropdown" onClick={(e) => e.stopPropagation()}>
                {user.email && !user.email.toLowerCase().endsWith('@kopmanpuzzel.nl') && (
                  <div className="user-email-box">{user.email}</div>
                )}
                {user.email?.toLowerCase().endsWith('@kopmanpuzzel.nl') && (
                  <button
                    className="link-email-btn"
                    onClick={() => setShowMigrationModal(true)}
                  >
                    Koppel E-mail
                  </button>
                )}
                <button className="logout-btn" onClick={handleLogout}>Uitloggen</button>
              </div>
            </div>
          ) : (
            <button className="login-btn" onClick={() => setShowLoginModal(true)}>Inloggen</button>
          )}
        </div>
      </header>

      {showManualModal && (
        <ManualRiderModal
          isOpen={showManualModal}
          onClose={() => setShowManualModal(false)}
          onSave={(newRiders) => { markGameModified(); setCustomRiders(newRiders); }}
          allRiders={riders}
          allRaces={races}
          currentCustomRiders={customRiders}
        />
      )}

      <div className="app-tabs">
        <button
          className={`tab-button ${activeTab === 'setup' ? 'active' : ''}`}
          onClick={() => setActiveTab('setup')}
          disabled={isGameModeLoading}
        >
          Stap 1: Favorieten Instellen
        </button>
        <button
          className={`tab-button ${activeTab === 'puzzle' ? 'active' : ''}`}
          onClick={handleAttemptSwitchToPuzzle}
          disabled={isGameModeLoading}
        >
          Stap 2: Puzzelen
        </button>
      </div>

      <div className="app-content">
        {activeTab === 'setup' && (
          <div className="setup-tab">
            <div className="setup-instructions">
              <div className="setup-header-row">
                <h2>Stap 1: {gameMode === 'klassiekers' ? 'Kies je favoriete kopmannen per wedstrijd' : 'Kies je favoriete renners per etappe'}</h2>
                {gameMode === 'klassiekers' && (
                  <button
                    className="manual-add-btn"
                    onClick={() => setShowManualModal(true)}
                    title="Voeg handmatig renners toe die niet op de lijst staan"
                  >
                    Voeg handmatig renners toe
                  </button>
                )}
                <button
                  className="overview-btn"
                  onClick={handleShowOverview}
                  disabled={isGameModeLoading}
                  title={gameMode === 'klassiekers' ? "Bekijk overzicht van alle renners en hun programma's" : "Bekijk overzicht van alle renners en wanneer ze geselecteerd zijn als favoriet"}
                >
                  {gameMode === 'klassiekers' ? "Programma's" : "Overzicht"}
                </button>
              </div>
            </div>

            {isGameModeLoading ? (
              <div className="rankings-loading" role="status" aria-live="polite">
                <div className="rankings-loading-spinner" />
                <span>Etappes en rankings laden...</span>
              </div>
            ) : (
              <div className="races-grid">
                {races.map(race => (
                  <RaceSelector
                    key={race}
                    gameMode={gameMode}
                    race={race}
                    favorites={favoriteRiders[race] || []}
                    onSetFavorite={handleSetFavorite}
                    onSetMultipleFavorites={handleSetMultipleFavorites}
                    onRemoveFavorite={handleRemoveFavorite}
                    invalidRiders={invalidFavorites[race] || []}
                    customRiders={customRiders.filter(cr => cr.race === race)}
                    allRiders={riders}
                    competitions={competitions}
                    apiUrl={API_URL}
                    isDropdownOpen={openDropdownRace === race}
                    onOpenDropdown={handleOpenDropdown}
                    onShowHistory={handleShowHistory}
                    onShowTop20={handleShowTop20}
                    allFavoriteRiders={favoriteRiders}
                    onCopyFavorites={handleCopyFavorites}
                    onOpenNotes={handleOpenNotes}
                    notes={userNotes}
                  />
                ))}
              </div>
            )}
            {!isGameModeLoading && <div className="setup-actions">
              <button
                className="submit-button"
                onClick={() => {
                  handleAttemptSwitchToPuzzle(); // Handles tab switch with warning
                }}
              >
                Volgende: Puzzelen
              </button>
            </div>}
          </div>
        )}

        {activeTab === 'puzzle' && (
          <div className={`puzzle-tab ${isGrandTour ? 'giro-layout-refined' : ''}`}>
            {isGrandTour ? (
              /* GIRO MODE: Split Header/Selection from Table */
              <>
                <div className="white-card">
                  <div className="puzzle-header">
                    <h2>Stap 2: Puzzel met je favoriete renners</h2>
                    <button
                      className="overview-btn"
                      onClick={handleShowOverview}
                  disabled={isGameModeLoading}
                      title="Bekijk overzicht van alle renners en wanneer ze geselecteerd zijn als favoriet"
                    >
                      Overzicht
                    </button>
                    <BudgetTracker
                      selectedRiders={selectedRiders}
                      riders={augmentedRiders}
                      totalBudget={totalBudget}
                      headerMode={true}
                      swapDemand={calculateSwapDemand(
                        activeConfig?.appliedSwaps,
                        activeConfig?.appliedSelectedSwapAfterRace,
                        activeConfig?.selectedRiders
                      )}
                    />
                  </div>

                  <div className="puzzle-container">
                    <RiderPuzzle
                      notes={userNotes}
                      gameMode={gameMode}
                      selectedRiders={selectedRiders}
                      onToggleRider={handleToggleRider}
                      riders={augmentedRiders}
                      favoriteRiders={favoriteRiders}
                      apiUrl={API_URL}
                      sortBy={sortBy}
                      onSortByChange={setSortBy}
                      filterFavorites={filterFavorites}
                      onFilterFavoritesChange={setFilterFavorites}
                      filterSelected={filterSelected}
                      onFilterSelectedChange={setFilterSelected}
                      selectedRaces={selectedRaces}
                      onSelectedRacesChange={setSelectedRaces}
                      selectedTeams={selectedTeams}
                      onSelectedTeamsChange={setSelectedTeams}
                      tableView={tableView}
                      onTableViewChange={setTableView}
                      searchTerm={searchTerm}
                      onSearchTermChange={setSearchTerm}
                      maxFavoritesRankPerRace={maxFavoritesRankPerRace}
                      configurations={configurations}
                      activeConfigId={activeConfigId}
                      onSwitchConfiguration={handleSwitchConfiguration}
                      onAddConfiguration={handleAddConfiguration}
                      onDeleteConfiguration={handleDeleteConfiguration}
                      isConfigurationOverBudget={isConfigurationOverBudget}
                      plannedSwaps={activeConfig?.plannedSwaps || [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }]}
                      selectedSwapAfterRace={activeConfig?.selectedSwapAfterRace || { '0': null, '1': null, '2': null }}
                      appliedSwaps={activeConfig?.appliedSwaps || [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }]}
                      appliedSelectedSwapAfterRace={activeConfig?.appliedSelectedSwapAfterRace || { '0': null, '1': null, '2': null }}
                      onUpdateSwaps={handleUpdateSwaps}
                      onUpdateConfigName={handleUpdateConfigName}
                      competitions={competitions}
                      onModalStateChange={setIsPuzzleModalOpen}
                      onShowTop20={handleShowTop20}
                      onShowHistory={handleShowHistory}
                      races={races}
                      renderParts="selection"
                      invalidRiderNames={allInvalidRiderNames}
                    />
                  </div>
                </div>

                {/* Outside part for Giro */}
                <RiderPuzzle
                  notes={userNotes}
                  gameMode={gameMode}
                  selectedRiders={selectedRiders}
                  onToggleRider={handleToggleRider}
                  riders={augmentedRiders}
                  favoriteRiders={favoriteRiders}
                  apiUrl={API_URL}
                  sortBy={sortBy}
                  onSortByChange={setSortBy}
                  filterFavorites={filterFavorites}
                  onFilterFavoritesChange={setFilterFavorites}
                  filterSelected={filterSelected}
                  onFilterSelectedChange={setFilterSelected}
                  selectedRaces={selectedRaces}
                  onSelectedRacesChange={setSelectedRaces}
                  selectedTeams={selectedTeams}
                  onSelectedTeamsChange={setSelectedTeams}
                  tableView={tableView}
                  onTableViewChange={setTableView}
                  searchTerm={searchTerm}
                  onSearchTermChange={setSearchTerm}
                  maxFavoritesRankPerRace={maxFavoritesRankPerRace}
                  configurations={configurations}
                  activeConfigId={activeConfigId}
                  onSwitchConfiguration={handleSwitchConfiguration}
                  onAddConfiguration={handleAddConfiguration}
                  onDeleteConfiguration={handleDeleteConfiguration}
                  isConfigurationOverBudget={isConfigurationOverBudget}
                  plannedSwaps={activeConfig?.plannedSwaps || [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }]}
                  selectedSwapAfterRace={activeConfig?.selectedSwapAfterRace || { '0': null, '1': null, '2': null }}
                  appliedSwaps={activeConfig?.appliedSwaps || [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }]}
                  appliedSelectedSwapAfterRace={activeConfig?.appliedSelectedSwapAfterRace || { '0': null, '1': null, '2': null }}
                  onUpdateSwaps={handleUpdateSwaps}
                  onUpdateConfigName={handleUpdateConfigName}
                  competitions={competitions}
                  onModalStateChange={setIsPuzzleModalOpen}
                  onShowTop20={handleShowTop20}
                  onShowHistory={handleShowHistory}
                  races={races}
                  renderParts="table"
                  totalBudget={totalBudget}
                  onOpenNotes={(r) => {
                    setNotesTarget(r);
                    setShowNotesModal(true);
                  }}
                  invalidRiderNames={allInvalidRiderNames}
                />

                <div className="giro-footer-actions">
                  <button
                    className="reset-button"
                    onClick={handleReset}
                    disabled={!activeConfig?.selectedRiders || activeConfig.selectedRiders.length === 0}
                  >
                    Opnieuw Beginnen
                  </button>
                </div>
              </>
            ) : (
              /* KLASSIEKERS MODE: Original white box */
              <>
                <div className="puzzle-header">
                  <h2>Stap 2: Puzzel met je favoriete renners</h2>
                  <button
                    className="overview-btn"
                    onClick={handleShowOverview}
                  disabled={isGameModeLoading}
                    title="Bekijk overzicht van alle renners en hun programma's"
                  >
                    Programma's
                  </button>
                  <BudgetTracker
                    selectedRiders={selectedRiders}
                    riders={augmentedRiders}
                    totalBudget={totalBudget}
                    headerMode={true}
                    swapDemand={calculateSwapDemand(
                      activeConfig?.appliedSwaps,
                      activeConfig?.appliedSelectedSwapAfterRace,
                      activeConfig?.selectedRiders
                    )}
                  />
                </div>

                <div className="puzzle-container">
                  <RiderPuzzle
                    notes={userNotes}
                    gameMode={gameMode}
                    selectedRiders={selectedRiders}
                    onToggleRider={handleToggleRider}
                    riders={augmentedRiders}
                    favoriteRiders={favoriteRiders}
                    apiUrl={API_URL}
                    sortBy={sortBy}
                    onSortByChange={setSortBy}
                    filterFavorites={filterFavorites}
                    onFilterFavoritesChange={setFilterFavorites}
                    filterSelected={filterSelected}
                    onFilterSelectedChange={setFilterSelected}
                    selectedRaces={selectedRaces}
                    onSelectedRacesChange={setSelectedRaces}
                    selectedTeams={selectedTeams}
                    onSelectedTeamsChange={setSelectedTeams}
                    tableView={tableView}
                    onTableViewChange={setTableView}
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                    maxFavoritesRankPerRace={maxFavoritesRankPerRace}
                    configurations={configurations}
                    activeConfigId={activeConfigId}
                    onSwitchConfiguration={handleSwitchConfiguration}
                    onAddConfiguration={handleAddConfiguration}
                    onDeleteConfiguration={handleDeleteConfiguration}
                    isConfigurationOverBudget={isConfigurationOverBudget}
                    plannedSwaps={activeConfig?.plannedSwaps || [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }]}
                    selectedSwapAfterRace={activeConfig?.selectedSwapAfterRace || { '0': null, '1': null, '2': null }}
                    appliedSwaps={activeConfig?.appliedSwaps || [{ out: '', in: '' }, { out: '', in: '' }, { out: '', in: '' }]}
                    appliedSelectedSwapAfterRace={activeConfig?.appliedSelectedSwapAfterRace || { '0': null, '1': null, '2': null }}
                    onUpdateSwaps={handleUpdateSwaps}
                    onUpdateConfigName={handleUpdateConfigName}
                    competitions={competitions}
                    onModalStateChange={setIsPuzzleModalOpen}
                    onShowTop20={handleShowTop20}
                    onShowHistory={handleShowHistory}
                    races={races}
                  />
                </div>

                <div className="puzzle-actions">
                  <button
                    className="reset-button"
                    onClick={handleReset}
                    disabled={!activeConfig?.selectedRiders || activeConfig.selectedRiders.length === 0}
                  >
                    Opnieuw Beginnen
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {modalStack.map((modal, index) => {
          const zIndex = 3000 + index;
          if (modal.type === 'overview') {
            return (
              <div key={modal.id} style={{ position: 'relative', zIndex }}>
                <RiderOverviewModal
                  isOpen={true}
                  onClose={(e) => handleCloseModal(modal.id, e)}
                  allRiders={augmentedRiders}
                  races={races}
                  competitions={competitions}
                  apiUrl={API_URL}
                  sortBy={overviewSortBy}
                  setSortBy={setOverviewSortBy}
                  activeRaceSorts={overviewActiveRaceSorts}
                  setActiveRaceSorts={setOverviewActiveRaceSorts}
                  showFavoritesFirst={overviewShowFavoritesFirst}
                  setShowFavoritesFirst={setOverviewShowFavoritesFirst}
                  favoriteRiders={favoriteRiders}
                  selectedRiders={selectedRiders}
                  onShowHistory={handleShowHistory}
                  modalId={modal.id}
                  showFavoritesOnly={gameMode !== 'klassiekers'} // Overzicht for giro, programma's for klassiekers
                  gameMode={gameMode}
                />
              </div>
            );
          }
          if (modal.type === 'top20') {
            return (
              <div key={modal.id} style={{ position: 'relative', zIndex }}>
                <HistoricalTop20Modal
                  raceName={modal.data.name}
                  raceId={modal.data.raceId}
                  historicalData={historicalData}
                  allRiders={augmentedRiders}
                  onRiderClick={handleShowRiderHistoryByName}
                  onClose={(e) => handleCloseModal(modal.id, e)}
                  modalId={modal.id}
                  onOpenNotes={(r) => {
                    setNotesTarget(r);
                    setShowNotesModal(true);
                  }}
                />
              </div>
            );
          }
          if (modal.type === 'history') {
            return (
              <div key={modal.id} style={{ position: 'relative', zIndex }}>
                <RiderHistory
                  riderName={modal.data.name}
                  riderId={modal.data.riderId}
                  teamAbbr={modal.data.team_abbr}
                  qualities={modal.data}
                  gameMode={gameMode}
                  competitions={competitions}
                  apiUrl={API_URL}
                  onShowTop20={handleShowTop20}
                  onClose={(e) => handleCloseModal(modal.id, e)}
                  modalId={modal.id}
                />
              </div>
            );
          }
          return null;
        })}

        {showNotesModal && (
          <NotesModal
            isOpen={showNotesModal}
            onClose={() => setShowNotesModal(false)}
            onOverlayClick={() => {
              if (modalStack.length > 0) {
                setModalStack([]);
              }
              setShowNotesModal(false);
            }}
            notes={userNotes}
            gameMode={gameMode}
            onUpdateNote={(cat, text) => { markGameModified(); setUserNotes(prev => ({ ...prev, [cat]: text })); }}
            races={races}
            initialTarget={notesTarget}
          />
        )}
      </div>

      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.type === 'success' && <span className="toast-icon"></span>}
          {toast.message}
        </div>
      )}

      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={cancelDeleteConfiguration}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p>Weet je zeker dat je deze selectie wilt verwijderen?</p>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-yes" onClick={confirmDeleteConfiguration}>
                Ja
              </button>
              <button className="delete-confirm-no" onClick={cancelDeleteConfiguration}>
                Nee
              </button>
            </div>
          </div>
        </div>
      )}

      {showResetConfirm && (
        <div className="delete-confirm-overlay" onClick={cancelReset}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p>Als je doorgaat worden alle geselecteerde renners en wissels in deze selectie gereset.</p>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-yes" onClick={confirmReset}>
                Doorgaan
              </button>
              <button className="delete-confirm-no" onClick={cancelReset}>
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvalidRiderWarning && (
        <div className="delete-confirm-overlay" onClick={cancelSwitchToPuzzle}>
          <div className="delete-confirm-modal warning-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#d32f2f' }}>Let op!</h3>
            {isGrandTour ? (
              <>
                <p>Je hebt een renner bij een etappe geselecteerd die de {grandTourName} niet (meer) rijdt.</p>
                <p>Verwijder deze renner uit de ranking(s).</p>
              </>
            ) : (
              <>
                <p>Je hebt een renner bij een koers geselecteerd die de betreffende koers niet (meer) rijdt.</p>
                <p>Je kunt deze renner handmatig aan de koers toevoegen, of uit de ranking verwijderen.</p>
              </>
            )}
            <p style={{ fontWeight: '600', marginTop: '1rem' }}>Wil je zonder aanpassingen doorgaan?</p>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-no" onClick={cancelSwitchToPuzzle}>
                Terug
              </button>
              <button className="delete-confirm-yes" onClick={confirmSwitchToPuzzle}>
                Doorgaan
              </button>
            </div>
          </div>
        </div>
      )}

      {showWelcomeMessage && (
        <div className="delete-confirm-overlay no-blur">
          <div className="delete-confirm-modal welcome-modal">
            <button
              className="welcome-close-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTimeout(() => setShowWelcomeMessage(false), 50);
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}
            >&times;</button>
            <h2 style={{ margin: '0 0 1.2rem 0', fontSize: '1.2rem', color: '#333' }}>
              {isGrandTour ? (
                <>Welkom bij Kopmanpuzzel {grandTourName}! <span className={`${gameMode}-dot`}></span></>
              ) : (
                <>Welkom bij Kopmanpuzzel!</>
              )}
            </h2>
            <p style={{ marginTop: '0.3rem', fontWeight: '600', fontSize: '0.9rem' }}>
              {currentSeo.visibleDescription}
            </p>
            <p style={{ marginTop: '0.3rem', fontSize: '0.85rem' }}>
              {isGrandTour
                ? 'Bij stap 1 stel je per wedstrijd je favorietenlijst per etappe samen om in stap 2 op basis van jouw ingevulde kennis te kunnen puzzelen.'
                : 'Bij stap 1 stel je per wedstrijd je gewenste kopmannenlijst samen om in stap 2 op basis van jouw ingevulde kennis te kunnen puzzelen.'}
            </p>
            {isGrandTour && (
              <p style={{ marginTop: '0.3rem', fontSize: '0.85rem' }}>
                Je kunt ook puzzelen in stap 2 zonder eerst stap 1 te voltooien.
              </p>
            )}

            <h3 style={{ marginTop: '1rem', marginBottom: '0.3rem', fontSize: '0.95rem' }}>
              {'\uD83D\uDD39'} Hoe werkt het?
            </h3>
            <ol style={{ marginTop: '0.3rem', marginLeft: '1.5rem', lineHeight: '1.6', fontSize: '0.85rem', textAlign: 'left' }}>
              {isGrandTour ? (
                <>
                  <li>Selecteer jouw favoriete renners per etappe.</li>
                  <li style={{ marginTop: '0.25rem' }}>Sorteer ze op volgorde van jouw voorkeur. Zet jouw absolute favoriet bovenaan!</li>
                  <li style={{ marginTop: '0.25rem' }}>Je bepaalt zelf hoe lang je de lijst maakt.</li>
                  <li style={{ marginTop: '0.25rem' }}>Kopieer gemakkelijk je ranking van een andere etappe.</li>
                  <li style={{ marginTop: '0.25rem' }}>
                    Klik op <span className="rider-history-btn rider-history-dropdown-btn" style={{ display: 'inline-flex', verticalAlign: 'middle', width: '14px', height: '10px', flexDirection: 'column', justifyContent: 'space-between', padding: '0', margin: '0 4px', pointerEvents: 'none', background: 'transparent', border: 'none', position: 'static', transform: 'none' }}>
                      <span style={{ display: 'block', width: '100%', height: '2px', background: '#bbb', borderRadius: '1px' }}></span>
                      <span style={{ display: 'block', width: '100%', height: '2px', background: '#bbb', borderRadius: '1px' }}></span>
                      <span style={{ display: 'block', width: '100%', height: '2px', background: '#bbb', borderRadius: '1px' }}></span>
                    </span> om de historische resultaten van een renner te bekijken.
                  </li>
                </>
              ) : (
                <>
                  <li>Open het menu bij een wedstrijd.</li>
                  <li style={{ marginTop: '0.25rem' }}>Selecteer jouw favoriete renners.</li>
                  <li style={{ marginTop: '0.25rem' }}>Sorteer ze op volgorde van jouw voorkeur. Zet jouw absolute favoriet bovenaan!</li>
                  <li style={{ marginTop: '0.25rem' }}>Je bepaalt zelf hoe lang je de lijst maakt.</li>
                  <li style={{ marginTop: '0.25rem' }}>
                    Klik op <span className="rider-history-btn rider-history-dropdown-btn" style={{ display: 'inline-flex', verticalAlign: 'middle', width: '14px', height: '10px', flexDirection: 'column', justifyContent: 'space-between', padding: '0', margin: '0 4px', pointerEvents: 'none', background: 'transparent', border: 'none', position: 'static', transform: 'none' }}>
                      <span style={{ display: 'block', width: '100%', height: '2px', background: '#bbb', borderRadius: '1px' }}></span>
                      <span style={{ display: 'block', width: '100%', height: '2px', background: '#bbb', borderRadius: '1px' }}></span>
                      <span style={{ display: 'block', width: '100%', height: '2px', background: '#bbb', borderRadius: '1px' }}></span>
                    </span> om de historische resultaten van een renner te bekijken.
                  </li>
                </>
              )}
            </ol>
            {!isGrandTour && (
              <p style={{ marginTop: '0.8rem', fontStyle: 'italic', fontSize: '0.85rem' }}>
                Voor het puzzelen in stap 2 kun je het beste minimaal 5 renners per wedstrijd selecteren.
              </p>
            )}

            <p style={{ marginTop: '1.2rem', fontSize: '0.9rem' }}>
              Ben je tevreden met je keuzes?
            </p>
            <p style={{ marginTop: '0.8rem', fontSize: '0.9rem' }}>
              <strong>Klik door naar Stap 2, daar begint het echte puzzelwerk!</strong> {'\uD83E\uDDE9'}
            </p>

            <p style={{ marginTop: '0.8rem', fontWeight: '600', color: '#667eea', fontSize: '0.9rem' }}>
              Om je favoriete renners en selecties op te slaan kun je eenvoudig een account aanmaken.
            </p>

            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <label style={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="dontShowAgain"
                  checked={dontShowAgainSetup}
                  onChange={(e) => setDontShowAgainSetup(e.target.checked)}
                />
                Dit bericht niet meer tonen
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
              <button
                className="welcome-btn"
                onClick={() => {
                  if (dontShowAgainSetup) {
                    localStorage.setItem(`dontShowWelcomeAgain_${gameMode}`, 'true');
                  }
                  setShowWelcomeMessage(false);
                }}
                style={{ flex: 1, backgroundColor: '#9e9e9e', color: 'white' }}
              >
                Begin zonder account
              </button>
              <button
                className="welcome-btn"
                onClick={switchToRegister}
                style={{ flex: 1, backgroundColor: '#4caf50', color: 'white' }}
              >
                Maak eerst een account aan
              </button>
            </div>
          </div>
        </div>
      )}

      {showPuzzleWelcomeMessage && (
        <div className="delete-confirm-overlay no-blur">
          <div className="delete-confirm-modal welcome-modal">
            <button
              className="welcome-close-btn"
              onClick={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                setTimeout(() => setShowPuzzleWelcomeMessage(false), 50); 
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>
            <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem', color: '#333' }}>
              🧩 Het echte puzzelen begint nu!
            </h2>
            <p style={{ marginTop: '0.3rem', fontWeight: '600', fontSize: '0.9rem' }}>
              {isGrandTour ? 'Tijd om jouw dreamteam samen te stellen!' : 'Tijd om jouw dreamteam samen te stellen!'}
            </p>
            <p style={{ marginTop: '0.3rem', fontSize: '0.85rem' }}>
              {isGrandTour
                ? 'Op deze pagina bouw je jouw selectie en houd je continu het budget én de dekking per etappe in de gaten.'
                : 'Op deze pagina bouw je jouw selectie en houd je continu het budget én de dekking per wedstrijd in de gaten.'}
            </p>

            <h3 style={{ marginTop: '1rem', marginBottom: '0.3rem', fontSize: '0.95rem' }}>
              {isGrandTour ? '📊 Wat zie je in de tabel?' : '📊 Wat zie je in de tabellen?'}
            </h3>
            <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '0.5rem 0 0.8rem 0' }} />

            <div style={{ fontSize: '0.85rem', textAlign: 'left', marginTop: '0.3rem' }}>
              {isGrandTour ? (
                <>
                  <h3 style={{ marginTop: '0.5rem', marginBottom: '0.3rem', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ display: 'inline-block', width: '15px', height: '15px', backgroundColor: '#4caf50', borderRadius: '50%' }}></span>
                    <span style={{ display: 'inline-block', width: '15px', height: '15px', backgroundColor: '#d32f2f', borderRadius: '50%' }}></span>
                    Favorieten
                  </h3>
                  <p style={{ marginBottom: '0.8rem' }}>
                    Hier zie je per etappe jouw eerder gekozen favorieten. Zitten ze al in je huidige selectie? Dan kleuren ze van groen tot rood, precies wat je wil!
                  </p>
                </>
              ) : (
                <>
                  <h3 style={{ marginTop: '0.5rem', marginBottom: '0.3rem', fontSize: '0.95rem' }}>
                    🔴 Dekking
                  </h3>
                  <p style={{ marginBottom: '0.8rem' }}>
                    In deze tabel zie je welke renners per wedstrijd meedoen op basis van jouw huidige selectie.
                    Renners die níét tot jouw favorieten behoren, worden rood weergegeven.
                  </p>

                  <h3 style={{ marginTop: '0.5rem', marginBottom: '0.3rem', fontSize: '0.95rem' }}>
                    🟢 Favorieten
                  </h3>
                  <p>
                    Hier zie je per wedstrijd jouw eerder gekozen favorieten.
                    Zitten ze al in je huidige selectie? Dan kleuren ze groen, precies wat je wil!
                  </p>
                </>
              )}
              <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '0.5rem 0 0.8rem 0' }} />
            </div>

            {isGrandTour && (
              <>
                <h3 style={{ marginTop: '1rem', marginBottom: '0.3rem', fontSize: '0.95rem', textAlign: 'center' }}>
                  ➕ Nieuwe selectie proberen?
                </h3>
                <p style={{ marginTop: '0.3rem', fontSize: '0.85rem', textAlign: 'center' }}>
                  Klik op het plusje om een nieuwe selectie te starten. Zo kun je verschillende strategieën vergelijken en de beste keuze maken.
                </p>

                <h3 style={{ marginTop: '1rem', marginBottom: '0.3rem', fontSize: '0.95rem', textAlign: 'center' }}>
                  📈 Uitslagen van jouw renners bekijken?
                </h3>
                <p style={{ marginTop: '0.3rem', fontSize: '0.85rem', textAlign: 'center' }}>
                  Klik op de naam van een renner in de tabel onderaan.
                </p>
              </>
            )}

            {gameMode === 'klassiekers' && (
              <>
                <h3 style={{ marginTop: '1rem', marginBottom: '0.3rem', fontSize: '0.95rem', textAlign: 'center' }}>
                  🔁 Wissels plannen?
                </h3>
                <p style={{ marginTop: '0.3rem', fontSize: '0.85rem', textAlign: 'center' }}>
                  Via de knop "Wissels" boven de tabel kun je geplande wissels instellen.
                  Beide tabellen passen zich automatisch aan, zodat je direct ziet wat het effect is.
                </p>

                <h3 style={{ marginTop: '1rem', marginBottom: '0.3rem', fontSize: '0.95rem', textAlign: 'center' }}>
                  ➕ Nieuwe selectie proberen?
                </h3>
                <p style={{ marginTop: '0.3rem', fontSize: '0.85rem', textAlign: 'center' }}>
                  Klik op het plusje om een nieuwe selectie te starten.
                  Zo kun je verschillende strategieën vergelijken en de beste keuze maken.
                </p>

                <h3 style={{ marginTop: '1rem', marginBottom: '0.3rem', fontSize: '0.95rem', textAlign: 'center' }}>
                  📈 Uitslagen van jouw renners bekijken?
                </h3>
                <p style={{ marginTop: '0.3rem', fontSize: '0.85rem', textAlign: 'center' }}>
                  Klik op de naam van een renner in de tabel onderaan.
                </p>
              </>
            )}

            <button
              className="submit-button"
              onClick={() => {
                setShowPuzzleWelcomeMessage(false);
              }}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              Laten we gaan!
            </button>
          </div>
        </div>
      )}

      {authModals}

    </div >
  );
}

export default App;
